import EngagedTopicDoneButton from '../components/EngagedTopicDoneButton'
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, ArrowRight, MessageSquarePlus, MoreVertical,
  Pin, ExternalLink, CheckCheck, CheckSquare, CircleCheck, Sparkles,
  Cpu, X, RotateCcw, Bell, BellOff, Maximize2, Minimize2, Search, Edit2,
  Volume2, VolumeX, MoonStar, Loader2, List, Mic,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  type Chat,
  type Agent,
  type ClaudeEffort,
  type CodexEffort,
  type CodexModel,
  type AskAnotherAgent,
  type ChatGptProHandoff,
  type Message,
  useChat, useChats, useEngagedTopics, useResetChat, useAskAnother, usePinChat, useMuteChat,
  useMuteTopicStartNotifications,
  useUpdateChatSessionTitle,
  useCloseSourceTodoist, useCloseTopic, useReopenTopic, useSnoozeChat, useSnoozeTopic, useUpdateTopic,
  useMarkChatRead, useSearch, useTopics, useMessages, suspendReadModelWarmers,
  useCodexAvailability,
  type SearchFilters, type SearchHit, type Topic,
} from '../lib/api'
import { laneStatusFriendly, topicStatusFriendly, TONE_BG } from '../lib/laneStatus'
import { useWsConnected } from '../lib/ws'
import { isFreeChat } from '../lib/chatListFilters'
import { fullDateTimeLabel, relativeLastReply, relativeTimeFromNow, relativeUptime } from '../lib/time'
import { topicDisplayTitle } from '../lib/topicTitles'
import { createF8Shortcut, handleChatNavigationShortcut } from '../lib/chatNavigationShortcut'
import { adjacentLaneId } from '../lib/laneNavigationOrder'
import { writeClipboardText } from '../lib/clipboard'
import { openExternalBrowser } from '../lib/externalBrowser'
import AgentBadge from '../components/AgentBadge'
import GuestShareButton from '../components/GuestShareButton'
import { codexAccountChip } from '../lib/codexAccounts'
import { liveVoiceAvailable } from '../lib/liveVoice'
import Composer, { type AutoVoiceReplyRequest } from '../components/Composer'
import CodeConversation from '../components/CodeConversation'
import MessageList from '../components/MessageList'
import DoneSoFarPanel from '../components/DoneSoFarPanel'
import SideJobsStrip from '../components/SideJobsStrip'
import StopTurnBar from '../components/StopTurnBar'
import { ReachableAction, ReachableActions } from '../components/ReachableActions'
import TopicTabs from '../components/TopicTabs'
import GroupListView from '../components/GroupListView'
import SearchFilterBar, { defaultSearchFilterState, searchFilterParams } from '../components/SearchFilterBar'
import SnoozePicker, { LANE_SNOOZE_PRESETS } from '../components/SnoozePicker'
import { draftTargetKey, useResolvedPaneMode, useUi } from '../store/ui'
import { clearReadNotificationsForChat } from '../lib/notificationClear'
import {
  loadCommitmentAddPill,
  loadCommitmentReviewPill,
  loadEmailSourcePill,
  loadEventTicketPill,
  loadTodoistSourcePill,
  loadTweetPreviewCard,
  chatUsesCommitmentAddPill,
  preloadThreadViewComponents,
  preloadThreadSourceComponents,
  topicUsesCommitmentReviewPill,
  topicUsesEmailSourcePill,
  topicUsesEventTicketPill,
  topicUsesTodoistSourcePill,
  topicUsesTweetPreviewCard,
} from '../lib/preloadChatViews'
import {
  backTarget,
  chatListTarget,
  chatTarget,
  chatTargetFromUrl,
  CODE_HOME,
  codeChatAgent,
  codeComposerAgent,
  codeTopicsForContext,
  isCodeChatId,
  X_LINKS_CHAT_ID,
} from '../lib/chatNavigation'
import { finishNavTiming } from '../lib/perfTelemetry'
import { shouldDispatchReadReceipt } from '../lib/readReceiptGate'
import { speakMessageAloud, stopReadAloud, useReadAloudState } from '../lib/readAloud'
import { latestMessageForImmediateReadAloud } from '../lib/readAloudText'
import { isAgentChatNativeApp } from '../lib/nativeExternalOpen'
import { startNotificationOpenTiming } from '../lib/notificationOpenDiagnostics'
import { commitmentActionStripVisible } from '../lib/topicChipVisibility'
import { messageVisibleInConversation } from '../lib/messageVisibility'
import {
  classifyTopic,
  loadTopicAdvanceContext,
  nextTopicIdAfterCurrent,
  nextTopicIdAfterRemovingCurrent,
  previousTopicIdBeforeCurrent,
} from '../lib/topicFilterCounts'
import { confirmTodoistClose, isTodoistSourceKind } from '../lib/todoistCloseAlert'

const LiveVoice = lazy(() => import('../components/LiveVoice'))
const TodoistSourcePill = lazy(loadTodoistSourcePill)
const EmailSourcePill = lazy(loadEmailSourcePill)
const CommitmentReviewPill = lazy(loadCommitmentReviewPill)
const CommitmentAddPill = lazy(loadCommitmentAddPill)
const EventTicketPill = lazy(loadEventTicketPill)
const TweetPreviewCard = lazy(loadTweetPreviewCard)

const WRITABLE_MODEL_OPTIONS = [
  { value: 'claude', label: 'Claude', detail: 'Claude CLI (claude --print)' },
  { value: 'codex', label: 'Codex', detail: 'Codex CLI, Codex 1 login (auto-fails over to Codex 2)' },
  { value: 'codex-secondary', label: 'Codex 2', detail: 'Codex CLI, second login' },
] as const

type WritableModel = (typeof WRITABLE_MODEL_OPTIONS)[number]['value']

const CLAUDE_EFFORT_OPTIONS: Array<{ value: ClaudeEffort; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra High' },
  { value: 'max', label: 'Max' },
  { value: 'ultracode', label: 'Ultracode' },
]

const CODEX_MODEL_OPTIONS: Array<{ value: CodexModel; label: string; efforts: CodexEffort[] }> = [
  { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', efforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] },
  { value: 'gpt-6.1-terra', label: 'GPT-6.1 Terra', efforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] },
  { value: 'gpt-6-astra', label: 'GPT-6 Astra', efforts: ['low', 'medium', 'high', 'xhigh', 'max', 'ultra'] },
]

const CODEX_EFFORT_OPTIONS: Array<{ value: CodexEffort; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra High' },
  { value: 'max', label: 'Max' },
  { value: 'ultra', label: 'Ultra' },
]

function normalizeClaudeEffort(value: string | null | undefined): ClaudeEffort {
  return CLAUDE_EFFORT_OPTIONS.some((option) => option.value === value)
    ? (value as ClaudeEffort)
    : 'high'
}

// Both Codex logins run every listed model (Codex 2 accepts Astra since
// 2026-09-21); the account no longer narrows the picker.
function codexModelOptionsForAgent(_agent: WritableModel) {
  return CODEX_MODEL_OPTIONS
}

function normalizeCodexModel(
  value: string | null | undefined,
  agent: WritableModel = 'codex',
): CodexModel {
  const options = codexModelOptionsForAgent(agent)
  return options.some((option) => option.value === value)
    ? (value as CodexModel)
    : options[0]?.value ?? 'gpt-6.1-sol'
}

function codexEffortsForModel(model: CodexModel): CodexEffort[] {
  return CODEX_MODEL_OPTIONS.find((option) => option.value === model)?.efforts ?? ['high']
}

function normalizeCodexEffort(
  value: string | null | undefined,
  model: CodexModel = 'gpt-6.1-sol',
): CodexEffort {
  const effort = CODEX_EFFORT_OPTIONS.some((option) => option.value === value)
    ? (value as CodexEffort)
    : 'high'
  return codexEffortsForModel(model).includes(effort) ? effort : 'high'
}

function chatDisplayTitle(chat: Chat): string {
  return chat.session_title || chat.display_name || chat.id
}

type ComposerFocusDecision = {
  scope: string
  value: boolean | null
}

const SHARE_TOPIC_COMPOSER_FOCUS_RETRY_DELAYS_MS = [0, 500, 1200, 2200, 3600]
const SHARE_TOPIC_COMPOSER_POST_CLEAR_FOCUS_DELAYS_MS = [150, 600, 1200]

function orderedTopicIdsForSection(
  topics: readonly Topic[],
  currentTopicId: string,
): string[] {
  const currentTopic = topics.find((topic) => topic.id === currentTopicId)
  const section = currentTopic ? classifyTopic(currentTopic) : 'active'
  const ordered = topics.filter((topic) => (
    topic.id === currentTopicId || classifyTopic(topic) === section
  ))

  if (section === 'snoozed') {
    ordered.sort((a, b) => (a.snoozed_until || 0) - (b.snoozed_until || 0))
  } else if (section === 'done') {
    ordered.sort((a, b) => (b.closed_at || 0) - (a.closed_at || 0))
  } else {
    ordered.sort((a, b) => (b.last_message_ts || b.created_at) - (a.last_message_ts || a.created_at))
  }

  return ordered.map((topic) => topic.id)
}

function searchHitTimeLabel(ts: number): string {
  try {
    return new Date(ts * 1000).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function threadHistoryScopeKey(chatId: string, topicId: string | null): string {
  return `${chatId}::${topicId ?? '__all__'}`
}

function threadHasOlderSearchScope(
  messages: readonly Message[] | undefined,
  chatId: string,
  topicId: string | null,
): boolean {
  const first = messages?.[0]
  return first?.has_older_messages === true &&
    first.history_scope_key === threadHistoryScopeKey(chatId, topicId)
}

function messageMatchesThreadSearchFilters(message: Message, filters: SearchFilters): boolean {
  if (filters.author === 'me' && message.direction !== 'inbound') return false
  if (
    filters.author === 'agentchat' &&
    message.direction !== 'outbound' &&
    message.direction !== 'agent' &&
    message.direction !== 'system'
  ) return false
  if (filters.fromTs !== null && filters.fromTs !== undefined && message.ts < filters.fromTs) return false
  if (filters.toTs !== null && filters.toTs !== undefined && message.ts > filters.toTs) return false
  return true
}

function currentThreadSearchHits(
  query: string,
  messages: readonly Message[] | undefined,
  chat: Chat,
  filters: SearchFilters,
): SearchHit[] {
  const q = query.trim().toLowerCase()
  if (q.length < 2) return []
  return (messages ?? [])
    .filter(messageVisibleInConversation)
    .filter((message) => message.body.toLowerCase().includes(q))
    .filter((message) => messageMatchesThreadSearchFilters(message, filters))
    .map((message) => ({
      message_id: message.id,
      chat_id: chat.id,
      topic_id: message.topic_id ?? null,
      direction: message.direction,
      user: message.user,
      ts: message.ts,
      body: message.body,
      chat_display_name: chat.display_name,
      platform_id: chat.platform_id,
      agent: chat.agent,
    }))
}

function ThreadSearchHitList({
  hits,
  onPick,
}: {
  hits: SearchHit[]
  onPick: (hit: SearchHit) => void
}) {
  return (
    <div className="max-h-[44dvh] overflow-y-auto">
      {hits.map((hit) => (
        <button
          key={hit.message_id}
          type="button"
          onClick={() => onPick(hit)}
          className="block w-full border-b border-stone-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-stone-100 dark:border-stone-800/70 dark:hover:bg-stone-800"
        >
          <div className="mb-1 flex items-center gap-2 text-xs text-stone-500">
            <AgentBadge agent={(hit.agent as Agent) || 'codex'} size={16} />
            <span className="min-w-0 flex-1 truncate">{hit.chat_display_name || hit.chat_id}</span>
            <span className="shrink-0">{hit.direction === 'inbound' ? 'Me' : 'AgentChat'}</span>
            <span className="shrink-0">{searchHitTimeLabel(hit.ts)}</span>
          </div>
          <div className="line-clamp-3 whitespace-pre-wrap break-words text-sm text-stone-800 dark:text-stone-100">
            {hit.body}
          </div>
        </button>
      ))}
    </div>
  )
}

type TopicLifecycle = {
  label: 'Active' | 'Snoozed' | 'Done'
  tone: 'active' | 'snoozed' | 'done'
  description: string
}

const TOPIC_LIFECYCLE_CHIP_CLASSES: Record<TopicLifecycle['tone'], string> = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
  snoozed: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800',
  done: 'bg-stone-200 text-stone-700 border-stone-300 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700',
}

// Header chip for a Codex login switch ("Codex 2 · failover"). Blue = Mark
// pinned it, amber = quota drove it, stone = plain Codex 2 conversation.
const CODEX_ACCOUNT_CHIP_CLASSES: Record<'blue' | 'amber' | 'stone', string> = {
  blue: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-800',
  amber: TOPIC_LIFECYCLE_CHIP_CLASSES.snoozed,
  stone: TOPIC_LIFECYCLE_CHIP_CLASSES.done,
}

function topicLifecycle(topic: Topic): TopicLifecycle {
  if (topic.closed) {
    return {
      label: 'Done',
      tone: 'done',
      description: topic.closed_at ? `Done ${relativeTimeFromNow(topic.closed_at)}` : 'Done',
    }
  }
  if (topic.is_snoozed) {
    return {
      label: 'Snoozed',
      tone: 'snoozed',
      description: topic.snoozed_until ? `Snoozed until ${fullDateTimeLabel(topic.snoozed_until)}` : 'Snoozed',
    }
  }
  return {
    label: 'Active',
    tone: 'active',
    description: 'Active topic',
  }
}

function topicTimeValue(epochSeconds: number | null | undefined): string | null {
  if (!epochSeconds) return null
  return `${relativeTimeFromNow(epochSeconds)} · ${fullDateTimeLabel(epochSeconds)}`
}

function TopicDetailsSheet({
  chatName,
  topic,
  onClose,
}: {
  chatName: string
  topic: Topic
  onClose: () => void
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const lifecycle = topicLifecycle(topic)
  const displayTitle = topicDisplayTitle(topic)
  const rows = [
    ['Group', chatName],
    ['State', lifecycle.description],
    ['Created', topicTimeValue(topic.created_at)],
    ['Last activity', topicTimeValue(topic.last_message_ts)],
    ['Last reply', topic.last_message_direction === 'agent' || topic.last_message_direction === 'outbound'
      ? topicTimeValue(topic.last_message_ts)
      : null],
    ['Snoozed until', topic.snoozed_until ? fullDateTimeLabel(topic.snoozed_until) : null],
    ['Closed', topicTimeValue(topic.closed_at)],
    ['Agent', topic.preferred_model || null],
    ['Source', topic.source_kind || null],
    ['Source ID', topic.source_task_id || null],
  ].filter((entry): entry is [string, string] => !!entry[1])

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-stone-950/50 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="topic-details-title"
      onClick={onClose}
      data-testid="topic-details-sheet"
    >
      <div
        className="w-full max-w-lg rounded-t-2xl border border-stone-200 bg-white shadow-2xl dark:border-stone-800 dark:bg-stone-900 sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-stone-200 px-4 py-3 dark:border-stone-800">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span
                className={clsx(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                  TOPIC_LIFECYCLE_CHIP_CLASSES[lifecycle.tone],
                )}
              >
                {lifecycle.label}
              </span>
            </div>
            <h2 id="topic-details-title" className="break-words text-base font-semibold leading-snug text-stone-950 dark:text-stone-50">
              {displayTitle}
            </h2>
            {topic.title && topic.title !== displayTitle && (
              <p className="mt-1 break-words text-xs text-stone-500 dark:text-stone-400">
                {topic.title}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close topic details"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <dl className="max-h-[60vh] overflow-y-auto px-4 py-3 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3 border-b border-stone-100 py-2 last:border-b-0 dark:border-stone-800">
              <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">{label}</dt>
              <dd className="min-w-0 break-words text-stone-800 dark:text-stone-200">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

function ThreadSearchResults({
  chat,
  chatId,
  topicId,
  query,
  currentMessages,
  scopeDone,
  onPick,
}: {
  chat: Chat
  chatId: string
  topicId: string | null
  query: string
  currentMessages: readonly Message[] | undefined
  scopeDone: boolean
  onPick: (hit: SearchHit) => void
}) {
  const trimmedQuery = query.trim()
  const [filters, setFilters] = useState(() => defaultSearchFilterState())
  const searchFilters = useMemo(() => searchFilterParams(filters), [filters])
  const [searchOlder, setSearchOlder] = useState(false)
  const filterScope = [
    searchFilters.includeDone ?? false,
    searchFilters.author ?? 'all',
    searchFilters.fromTs ?? '',
    searchFilters.toTs ?? '',
  ].join(':')
  useEffect(() => {
    setSearchOlder(false)
  }, [chatId, topicId, trimmedQuery, filterScope])
  const currentHits = useMemo(
    () => scopeDone && !searchFilters.includeDone
      ? []
      : currentThreadSearchHits(trimmedQuery, currentMessages, chat, searchFilters),
    [chat, currentMessages, scopeDone, searchFilters, trimmedQuery],
  )
  const currentHitIds = useMemo(
    () => new Set(currentHits.map((hit) => hit.message_id)),
    [currentHits],
  )
  const canSearchOlder = threadHasOlderSearchScope(currentMessages, chatId, topicId)
  const { data: olderData, isLoading, error } = useSearch(
    trimmedQuery,
    { chatId, topicId },
    searchFilters,
    { enabled: searchOlder },
  )
  const olderHits = useMemo(
    () => (olderData ?? []).filter((hit) => !currentHitIds.has(hit.message_id)),
    [currentHitIds, olderData],
  )
  const showCurrentSearchState = trimmedQuery.length >= 2 && currentHits.length === 0 && !searchOlder

  return (
    <div className="shrink-0 border-b border-stone-200 bg-white/95 shadow-sm dark:border-stone-800 dark:bg-stone-900/95">
      <SearchFilterBar filters={filters} onChange={setFilters} className="px-4 py-2" />
      {trimmedQuery.length < 2 && (
        <div className="px-4 py-3 text-sm text-stone-500">Type at least 2 characters to search here.</div>
      )}
      {currentHits.length > 0 && (
        <ThreadSearchHitList hits={currentHits} onPick={onPick} />
      )}
      {showCurrentSearchState && (
        <div className="px-4 py-3 text-sm text-stone-500">No matches in current messages.</div>
      )}
      {trimmedQuery.length >= 2 && canSearchOlder && !searchOlder && (
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={() => setSearchOlder(true)}
            className="inline-flex min-h-9 items-center rounded-md border border-stone-200 bg-white px-3 text-sm font-medium text-stone-700 shadow-sm active:bg-stone-100 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-200"
          >
            Search older messages
          </button>
        </div>
      )}
      {trimmedQuery.length >= 2 && searchOlder && isLoading && (
        <div className="px-4 py-3 text-sm text-stone-500">Searching older messages…</div>
      )}
      {trimmedQuery.length >= 2 && searchOlder && error && (
        <div className="px-4 py-3 text-sm text-red-600">Older search failed: {String((error as Error).message)}</div>
      )}
      {trimmedQuery.length >= 2 && searchOlder && !isLoading && !error && olderHits.length === 0 && (
        <div className="px-4 py-3 text-sm text-stone-500">No older matches.</div>
      )}
      {olderHits.length > 0 && (
        <ThreadSearchHitList hits={olderHits} onPick={onPick} />
      )}
    </div>
  )
}

function shouldAutofocusComposer(chat: Chat, messages: Message[], activeTopicId: string | null): boolean {
  if (activeTopicId) return false

  const sessionAnchor = Math.max(chat.created_at || 0, chat.last_reset_at || 0)
  if (!sessionAnchor) return false

  const hasUserOrAgentMessage = messages.some((m) => {
    if (m.deleted || m.ts < sessionAnchor) return false
    return m.direction === 'inbound' || m.direction === 'outbound' || m.direction === 'agent'
  })

  return !hasUserOrAgentMessage
}

export default function ChatPage() {
  const { chatId } = useParams<{ chatId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { data: chat, isLoading, error: chatError } = useChat(chatId)
  const { data: allChats } = useChats({ poll: false })
  const wsConnected = useWsConnected()
  // Only Codex conversations can be moved between logins; skip the read for
  // everything else so Claude/Cursor/Gemini chats never fetch it.
  const chatRuntimeIsCodex = chat?.agent === 'codex' || chat?.agent === 'codex-secondary'
    || chat?.preferred_model === 'codex' || chat?.preferred_model === 'codex-secondary'
  const { data: codexAvailability } = useCodexAvailability(chatRuntimeIsCodex)
  const reset = useResetChat(chatId)
  const askAnother = useAskAnother(chatId)
  const pinChat = usePinChat(chatId)
  const muteChat = useMuteChat(chatId)
  const muteTopicStart = useMuteTopicStartNotifications(chatId)
  const updateChatSessionTitle = useUpdateChatSessionTitle(chatId)
  const closeTodoist = useCloseSourceTodoist(chatId)
  const closeTopic = useCloseTopic(chatId)
  const reopenTopic = useReopenTopic(chatId)
  const snoozeChat = useSnoozeChat(chatId)
  const snoozeTopic = useSnoozeTopic(chatId)
  const updateTopic = useUpdateTopic(chatId)
  const { mutate: markChatRead, isPending: markReadPending } = useMarkChatRead(chatId)
  const resolvedPaneMode = useResolvedPaneMode()
  const chatFullWindow = useUi((s) => s.chatFullWindow)
  const setChatFullWindow = useUi((s) => s.setChatFullWindow)
  const readAloudAuto = useUi((s) => s.readAloudAuto)
  const setReadAloudAuto = useUi((s) => s.setReadAloudAuto)
  const readAloudAutoListen = useUi((s) => s.readAloudAutoListen)
  const setReadAloudAutoListen = useUi((s) => s.setReadAloudAutoListen)
  const readAloudSpeed = useUi((s) => s.readAloudSpeed)
  const readAloudVoice = useUi((s) => s.readAloudVoice)
  const readAloud = useReadAloudState()
  const [autoVoiceReplyRequest, setAutoVoiceReplyRequest] =
    useState<AutoVoiceReplyRequest | null>(null)
  const [backgroundTopicActionError, setBackgroundTopicActionError] = useState<string | null>(null)
  const readAloudAutoListenRef = useRef(readAloudAutoListen)
  const autoVoiceReplyScopeRef = useRef('')
  const latestImmediateReadAloudMessageRef = useRef<Message | null>(null)
  const pendingImmediateReadAloudScopeRef = useRef<string | null>(null)
  useEffect(() => {
    readAloudAutoListenRef.current = readAloudAutoListen
  }, [readAloudAutoListen])
  const effectiveWidth = useUi((s) => s.effectiveWidth)
  const showFullWindowToggle = useMemo(
    () => effectiveWidth >= 560 && (resolvedPaneMode === 'split' || chatFullWindow),
    [chatFullWindow, effectiveWidth, resolvedPaneMode],
  )
  const freeLaneTargets = useMemo(
    () => (allChats ?? []).filter((candidate) => (
      candidate.id !== chatId
      && !candidate.archived
      && isFreeChat(candidate, wsConnected)
    )),
    [allChats, chatId, wsConnected],
  )

  // activeTopicId is synced with the ?topic_id= URL param so the URL is the
  // single source of truth for which topic is open.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlTopicId = searchParams.get('topic_id') || searchParams.get('topic')
  const activeTopicId = urlTopicId || null
  const { data: engagedHeaderTopics } = useEngagedTopics(effectiveWidth >= 1024 && !!activeTopicId)
  useEffect(() => setBackgroundTopicActionError(null), [chatId])
  const newTopicShareParam = searchParams.get('new_topic_share')
  const newTopicShareId = newTopicShareParam && /^\d+$/.test(newTopicShareParam)
    ? Number(newTopicShareParam)
    : null
  const newTopicPrompt = searchParams.get('new_topic_prompt')?.trim() || ''
  const focusComposerOnOpen = searchParams.get('focus_composer') === '1'
  const notificationOpen = searchParams.get('notification_open') === '1'
  const rawTargetMessageId = searchParams.get('message_id') || searchParams.get('message') || null
  const notificationTargetMessageId = notificationOpen ? rawTargetMessageId : null
  const targetMessageId = rawTargetMessageId
  // Notification opens get their exact target window from the atomic bundle;
  // search/deep links use the normal around-message endpoint.
  const aroundMessageId = targetMessageId && !notificationOpen ? targetMessageId : null
  const normalLatestOpen = !notificationOpen && !targetMessageId && !aroundMessageId
  const notificationTimingScopeRef = useRef('')
  useLayoutEffect(() => {
    if (!notificationOpen || !chatId) return
    suspendReadModelWarmers()
    const scope = `${chatId}:${activeTopicId ?? ''}:${rawTargetMessageId ?? ''}`
    if (notificationTimingScopeRef.current === scope) return
    notificationTimingScopeRef.current = scope
    const nativeApp = isAgentChatNativeApp()
    startNotificationOpenTiming({
      source: nativeApp ? 'native' : 'direct',
      chatId,
      topicId: activeTopicId ?? null,
      messageId: rawTargetMessageId,
      // MainActivity can cold-load notification routes before the JS push
      // listener exists. performance.now() starts at navigation, so 0 captures
      // the WebView cold-start cost instead of only the React render tail.
      startMs: 0,
      nativePlatform: nativeApp,
    })
  }, [activeTopicId, chatId, notificationOpen, rawTargetMessageId])
  const setActiveTopicId = useCallback((id: string | null, options?: { replace?: boolean; focusComposer?: boolean }) => {
    const replace = options?.replace ?? false
    if (id) {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        if (chatId) p.set('chat_id', chatId)
        p.delete('topic')
        p.set('topic_id', id)
        p.delete('message')
        p.delete('message_id')
        p.delete('notification_open')
        if (options?.focusComposer) p.set('focus_composer', '1')
        else p.delete('focus_composer')
        return p
      }, { replace })
    } else {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        if (chatId) p.set('chat_id', chatId)
        p.delete('topic')
        p.delete('topic_id')
        p.delete('message')
        p.delete('message_id')
        p.delete('notification_open')
        p.delete('focus_composer')
        return p
      }, { replace })
    }
  }, [chatId, setSearchParams])

  useEffect(() => {
    if (!focusComposerOnOpen || !activeTopicId || !chatId) return
    const focusEventDetail = { chatId, draftKey: draftTargetKey(chatId, activeTopicId) }
    const timers = SHARE_TOPIC_COMPOSER_FOCUS_RETRY_DELAYS_MS.map((delayMs, index) =>
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('agentchat:focus-composer', {
          detail: focusEventDetail,
        }))
        if (index !== SHARE_TOPIC_COMPOSER_FOCUS_RETRY_DELAYS_MS.length - 1) return
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev)
          p.delete('focus_composer')
          return p
        }, { replace: true })
        SHARE_TOPIC_COMPOSER_POST_CLEAR_FOCUS_DELAYS_MS.forEach((postClearDelayMs) => {
          window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent('agentchat:focus-composer', {
              detail: focusEventDetail,
            }))
          }, postClearDelayMs)
        })
      }, delayMs),
    )
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [activeTopicId, chatId, focusComposerOnOpen, setSearchParams])

  // Topics for auto-spawn group chats. Keep this above the message query:
  // unread topic opens must fetch around the oldest unread row before the
  // auto mark-read mutation clears the unread flags.
  const { data: topics } = useTopics(chatId)
  const isAutoSpawnTopic = chat?.type === 'auto-spawn' && !!activeTopicId
  const activeTopic = useMemo(() => {
    if (!isAutoSpawnTopic) return null
    return (topics || []).find((t) => t.id === activeTopicId) ?? null
  }, [activeTopicId, isAutoSpawnTopic, topics])
  const [topicDetailsOpen, setTopicDetailsOpen] = useState(false)
  useEffect(() => { setTopicDetailsOpen(false) }, [activeTopicId])
  useEffect(() => { setRenamingChat(false) }, [chatId])
  useEffect(() => {
    closeTopic.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopicId])
  const topicTitles = useMemo(() => (
    (topics || []).map((topic) => topic.title)
  ), [topics])
  const openUnreadCount = activeTopicId
    ? activeTopic?.unread
    : chat?.unread
  const isGroupRoot = chat?.type === 'auto-spawn' && !activeTopicId
  // A coding group has no root of its own: its conversations live in the Code
  // tab. Every way to land here with no topic selected — a bare
  // /chats/pwa-auto-code-* URL, or closeActiveTopicAndAdvance falling through to
  // setActiveTopicId(null) when the last active topic closes — resolves to the
  // Code tab through this one redirect, so GroupListView is never reachable for
  // these chats.
  const redirectToCodeTab = !!chatId && isCodeChatId(chatId) && !activeTopicId
  useEffect(() => {
    if (!redirectToCodeTab) return
    navigate(CODE_HOME, { replace: true })
  }, [navigate, redirectToCodeTab])
  const messagesForOpenEnabled = !!chat && !isGroupRoot
  useEffect(() => {
    autoVoiceReplyScopeRef.current = `${chatId ?? ''}:${activeTopicId ?? ''}`
  }, [activeTopicId, chatId])
  const groupUnreadCount = useMemo(() => {
    if (chat?.type !== 'auto-spawn') return 0
    if (topics) {
      return topics.reduce((sum, topic) => (
        !topic.closed && (topic.unread ?? 0) > 0 ? sum + 1 : sum
      ), 0)
    }
    return chat.unread_topic_count ?? 0
  }, [chat?.type, chat?.unread_topic_count, topics])

  useEffect(() => {
    if (!chat) return
    if (isGroupRoot) return
    const id = window.setTimeout(() => {
      preloadThreadViewComponents()
      preloadThreadSourceComponents({
        chatId,
        sourceKind: activeTopic?.source_kind,
      })
    }, 0)
    return () => window.clearTimeout(id)
  }, [activeTopic?.source_kind, chat, chatId, isGroupRoot])

  // Where the header back arrow goes. Resolved from the URL by the same pure
  // backTarget() the Android hardware back uses (App.tsx), so the two buttons
  // are always identical: topic → group/lane homepage → Lanes home. On a chat
  // route backTarget never returns null, so the arrow always has a target.
  const upTarget = backTarget(location.pathname, location.search)
  const leaveChatRoute = useCallback((target: string) => {
    navigate(target, { replace: true })
  }, [navigate])
  const goUp = useCallback(() => {
    if (upTarget) leaveChatRoute(upTarget)
  }, [leaveChatRoute, upTarget])

  const [showKebab, setShowKebab] = useState(false)
  const [liveVoiceScope, setLiveVoiceScope] = useState<string | null>(null)
  const voiceScope = `${chatId}:${activeTopicId ?? 'lane'}`
  const canUseLiveVoice = Boolean(chat && !chat.archived && (
    (chat.type === 'user-driven' && !activeTopicId) || (activeTopic && !activeTopic.closed)
  ))
  const { data: voiceAvailable } = useQuery({
    queryKey: ['live_voice_available'],
    queryFn: liveVoiceAvailable,
    enabled: canUseLiveVoice,
    staleTime: 60_000,
    retry: false,
  })
  const [showFreeLaneMenu, setShowFreeLaneMenu] = useState(false)
  const [showSnoozePicker, setShowSnoozePicker] = useState(false)
  const [showLaneSnoozePicker, setShowLaneSnoozePicker] = useState(false)
  const [renamingTopic, setRenamingTopic] = useState(false)
  const [renamingChat, setRenamingChat] = useState(false)
  const [chatRenameDraft, setChatRenameDraft] = useState('')
  const [renameDraft, setRenameDraft] = useState('')
  const [askingAnother, setAskingAnother] = useState(false)
  const [askText, setAskText] = useState('')
  const [proHandoff, setProHandoff] = useState<ChatGptProHandoff | null>(null)
  const [claudeEffortDialog, setClaudeEffortDialog] = useState<{
    chatName: string
    initialEffort: ClaudeEffort
  } | null>(null)
  const [codexEffortDialog, setCodexEffortDialog] = useState<{
    chatName: string
    model: WritableModel
    initialCodexModel: CodexModel
    initialEffort: CodexEffort
  } | null>(null)
  const [modelSwitchPending, setModelSwitchPending] = useState(false)
  const kebabRef = useRef<HTMLDivElement | null>(null)
  const kebabButtonRef = useRef<HTMLButtonElement | null>(null)
  const nextTopicButtonRef = useRef<HTMLButtonElement | null>(null)
  const backButtonRef = useRef<HTMLButtonElement | null>(null)
  const f8ActionButtonRef = useRef<HTMLButtonElement | null>(null)
  const skipChatRenameBlurRef = useRef(false)
  const topicRenameDialogRef = useRef<HTMLDialogElement | null>(null)
  const topicRenamePendingRef = useRef(false)

  const startTopicRename = () => {
    if (!activeTopic) return
    updateTopic.reset()
    setRenameDraft(activeTopic.title || '')
    setRenamingTopic(true)
  }

  const submitTopicRename = () => {
    if (!activeTopic || topicRenamePendingRef.current) return
    const title = renameDraft.trim()
    if (!title) return
    if (title === activeTopic.title) {
      setRenamingTopic(false)
      return
    }
    topicRenamePendingRef.current = true
    updateTopic.mutate({ topic_id: activeTopic.id, title }, {
      onSuccess: () => {
        setRenamingTopic(false)
      },
      onSettled: () => { topicRenamePendingRef.current = false },
    })
  }

  useEffect(() => { setRenamingTopic(false) }, [activeTopicId])

  useEffect(() => {
    const dialog = topicRenameDialogRef.current
    if (!renamingTopic || !dialog) return
    dialog.showModal()
    return () => dialog.close()
  }, [renamingTopic])

  const submitChatRename = useCallback(() => {
    if (!chat || chat.type === 'auto-spawn') {
      setRenamingChat(false)
      return
    }
    const nextTitle = chatRenameDraft.trim()
    const currentTitle = chat.session_title || ''
    if (nextTitle !== currentTitle) {
      updateChatSessionTitle.mutate(nextTitle || null)
    }
    setRenamingChat(false)
  }, [chat, chatRenameDraft, updateChatSessionTitle])

  const updateGroupDefaultModel = useCallback(async (
    preferredModel: WritableModel,
    claudeEffort?: ClaudeEffort,
    codexModel?: CodexModel,
    codexEffort?: CodexEffort,
  ) => {
    if (!chat) return
    const patch = claudeEffort
      ? { preferred_model: preferredModel, claude_effort: claudeEffort }
      : codexModel || codexEffort
        ? { preferred_model: preferredModel, codex_model: codexModel, codex_effort: codexEffort }
        : { preferred_model: preferredModel }
    const res = await fetch(`/api/chats/${chat.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw new Error(await res.text())
    queryClient.setQueryData<Chat>(['chat', chat.id], (cur) => (
      cur ? { ...cur, ...patch } : cur
    ))
    queryClient.setQueryData<Chat[]>(['chats'], (cur) => (
      cur?.map((row) => (row.id === chat.id ? { ...row, ...patch } : row))
    ))
    queryClient.invalidateQueries({ queryKey: ['chat', chat.id] })
    queryClient.invalidateQueries({ queryKey: ['chats'] })
  }, [chat, queryClient])

  // Tweet preview visibility — collapsed by default per Mark's feedback
  // ("its taking up too much space"). The Tweet button in the source
  // pill toggles it on/off; the in-card "Open in X" footer link is the
  // navigation affordance once shown. Resets on topic change so each
  // topic starts collapsed (the default that respects screen real
  // estate on a phone).
  const [tweetPreviewOpen, setTweetPreviewOpen] = useState(false)
  const [groupSearchOpen, setGroupSearchOpen] = useState(false)
  const [groupSearchQuery, setGroupSearchQuery] = useState('')
  const [threadSearchOpen, setThreadSearchOpen] = useState(false)
  const [threadSearchQuery, setThreadSearchQuery] = useState('')
  useEffect(() => { setTweetPreviewOpen(false) }, [activeTopicId])
  useEffect(() => {
    if (activeTopicId) { setGroupSearchOpen(false); setGroupSearchQuery('') }
  }, [activeTopicId])
  useEffect(() => {
    setThreadSearchOpen(false)
    setThreadSearchQuery('')
  }, [chatId, activeTopicId])
  useEffect(() => {
    if (!isGroupRoot) return
    setThreadSearchOpen(false)
    setThreadSearchQuery('')
  }, [isGroupRoot])
  const headerSearchOpen = groupSearchOpen || threadSearchOpen
  const toggleTweetPreview = useCallback(() => setTweetPreviewOpen((v) => !v), [])
  const toggleFullWindow = useCallback(() => {
    setChatFullWindow(!chatFullWindow)
  }, [chatFullWindow, setChatFullWindow])
  const requestImmediateAutoVoiceReply = useCallback(() => {
    setAutoVoiceReplyRequest({
      id: `manual:${Date.now()}`,
      replyToMessageId: null,
    })
  }, [])
  const speakLatestMessageInCurrentScope = useCallback(() => {
    if (document.visibilityState !== 'visible') return
    const message = latestImmediateReadAloudMessageRef.current
    const scope = `${chatId ?? ''}:${activeTopicId ?? ''}`
    if (!message) {
      pendingImmediateReadAloudScopeRef.current = scope
      return
    }
    pendingImmediateReadAloudScopeRef.current = null
    void speakMessageAloud({
      messageId: message.id,
      body: message.body,
      source: 'auto',
      speed: readAloudSpeed,
      voice: readAloudVoice,
    })
  }, [activeTopicId, chatId, readAloudSpeed, readAloudVoice])
  const toggleReadAloudAuto = useCallback(() => {
    const next = !readAloudAuto
    setReadAloudAuto(next)
    if (next) {
      speakLatestMessageInCurrentScope()
      return
    }
    if (!next) {
      setReadAloudAutoListen(false)
      setAutoVoiceReplyRequest(null)
      pendingImmediateReadAloudScopeRef.current = null
      if (readAloud.source === 'auto') stopReadAloud()
    }
  }, [
    readAloud.source,
    readAloudAuto,
    setReadAloudAuto,
    setReadAloudAutoListen,
    speakLatestMessageInCurrentScope,
  ])
  const toggleReadAloudAutoListen = useCallback(() => {
    const next = !readAloudAutoListen
    if (next && !readAloudAuto) setReadAloudAuto(true)
    setReadAloudAutoListen(next)
    if (next) {
      requestImmediateAutoVoiceReply()
      return
    }
    if (!next) setAutoVoiceReplyRequest(null)
  }, [
    readAloudAuto,
    readAloudAutoListen,
    requestImmediateAutoVoiceReply,
    setReadAloudAuto,
    setReadAloudAutoListen,
  ])
  useEffect(() => {
    if (!readAloudAuto || isGroupRoot) return
    function onAgentReplyFinal(event: Event) {
      const detail = (event as CustomEvent<{
        chatId?: string
        topicId?: string | null
        messageId?: string
        body?: string
      }>).detail
      if (!detail?.chatId || !detail.messageId || typeof detail.body !== 'string') return
      if (detail.chatId !== chatId) return
      if ((detail.topicId ?? null) !== (activeTopicId ?? null)) return
      if (document.visibilityState !== 'visible') return
      const requestScope = `${detail.chatId}:${detail.topicId ?? ''}`
      const messageId = detail.messageId
      const body = detail.body
      void (async () => {
        const played = await speakMessageAloud({
          messageId,
          body,
          source: 'auto',
          speed: readAloudSpeed,
          voice: readAloudVoice,
        })
        if (!played || !readAloudAutoListenRef.current) return
        if (document.visibilityState !== 'visible') return
        if (autoVoiceReplyScopeRef.current !== requestScope) return
        setAutoVoiceReplyRequest({
          id: `${messageId}:${Date.now()}`,
          replyToMessageId: messageId,
        })
      })()
    }
    window.addEventListener('agentchat:agent-reply-final', onAgentReplyFinal)
    return () => window.removeEventListener('agentchat:agent-reply-final', onAgentReplyFinal)
  }, [
    activeTopicId,
    chatId,
    isGroupRoot,
    readAloudAuto,
    readAloudAutoListen,
    readAloudSpeed,
    readAloudVoice,
  ])

  // Keyboard policy: focus only fresh empty chats and topics opened from an
  // explicit share-to-X-Links creation intent. Existing chats and normal topic
  // opens stay read-first with the keyboard hidden.
  const { data: messagesForFocusDecision, isFetching: messagesFetching } = useMessages(
    chatId,
    activeTopicId,
    {
      enabled: messagesForOpenEnabled,
      notificationOpen,
      notificationTargetMessageId,
      latestWindowOnly: normalLatestOpen,
    },
  )
  const latestImmediateReadAloudMessage = useMemo(
    () => latestMessageForImmediateReadAloud(messagesForFocusDecision),
    [messagesForFocusDecision],
  )
  const focusScope = useMemo(() => [
    chatId || '',
    activeTopicId || 'chat',
    chat?.created_at || 0,
    chat?.last_reset_at || 0,
    focusComposerOnOpen ? 'composer-focus' : 'read-first',
  ].join(':'), [activeTopicId, chat?.created_at, chat?.last_reset_at, chatId, focusComposerOnOpen])
  const composerAutoFocusRef = useRef<ComposerFocusDecision>({ scope: '', value: null })
  if (composerAutoFocusRef.current.scope !== focusScope) {
    composerAutoFocusRef.current = {
      scope: focusScope,
      value: activeTopicId ? focusComposerOnOpen : null,
    }
  }
  if (
    composerAutoFocusRef.current.value === null
    && chat
    && messagesForFocusDecision
    && !messagesFetching
  ) {
    composerAutoFocusRef.current.value = shouldAutofocusComposer(
      chat,
      messagesForFocusDecision,
      activeTopicId,
    )
  }
  const composerAutoFocus = composerAutoFocusRef.current.value

  useEffect(() => {
    if (!chatId || !isGroupRoot || topics === undefined) return
    const id = window.requestAnimationFrame(() => {
      finishNavTiming('group-open', chatId, { topics: topics.length })
    })
    return () => window.cancelAnimationFrame(id)
  }, [chatId, isGroupRoot, topics])

  useEffect(() => {
    if (!chatId || chat?.type === 'auto-spawn' || isGroupRoot) return
    if (!messagesForFocusDecision || messagesFetching || composerAutoFocus === null) return
    const id = window.requestAnimationFrame(() => {
      finishNavTiming('chat-open', chatId, { messages: messagesForFocusDecision.length })
    })
    return () => window.cancelAnimationFrame(id)
  }, [
    chat?.type,
    chatId,
    composerAutoFocus,
    isGroupRoot,
    messagesFetching,
    messagesForFocusDecision,
  ])

  useEffect(() => {
    if (!chatId || !activeTopicId || topics === undefined) return
    if (!messagesForFocusDecision || messagesFetching || composerAutoFocus === null) return
    const id = window.requestAnimationFrame(() => {
      finishNavTiming('topic-open', `${chatId}:${activeTopicId}`, {
        messages: messagesForFocusDecision.length,
      })
    })
    return () => window.cancelAnimationFrame(id)
  }, [
    activeTopicId,
    chatId,
    composerAutoFocus,
    messagesFetching,
    messagesForFocusDecision,
    topics,
  ])

  // Auto-mark-read on manual chat/topic open AND on every new agent reply
  // that lands while that readable scope stays open. The group root posts the
  // existing unscoped read receipt, which clears only root-level rows for
  // auto-spawn chats; unread topic rows still require opening the topic.
  //
  // First fire is GATED on the autoFocus decision so mark-read can't race
  // the useMessages SELECT — without that, on a cold cache the UPDATE
  // commits first, the SELECT then returns every row with unread=0, the
  // smart-keyboard sees unreadCount === 0 and autofocuses, and the
  // keyboard pops under Mark's reading thumb on every unread chat.
  //
  // Subsequent fires are tied to the messages-list LENGTH growing while
  // the chat is open — each new agent_reply (or agent_reply_delta first-
  // packet) inserts exactly one row, so length is the right edge to
  // detect. Streaming deltas after the first one only update the body
  // of an existing row, so they don't re-trigger this. Without this
  // re-fire, the chat-list badge for the chat Mark is currently inside
  // ticks up to 1 the moment a reply arrives — which is exactly the
  // "badge for an active chat" bug.
  const lastMarkedRef = useRef<string | undefined>(undefined)
  const lastMessageCountRef = useRef<number>(0)
  const lastLatestMessageIdRef = useRef<string>('')
  const messageCount = messagesForFocusDecision?.length ?? 0
  const latestMessageId = messagesForFocusDecision?.[Math.max(0, messageCount - 1)]?.id ?? ''
  const unreadMessageCount = useMemo(() => (
    messagesForFocusDecision?.reduce((sum, message) => (
      sum + (message.unread && !message.deleted ? 1 : 0)
    ), 0) ?? 0
  ), [messagesForFocusDecision])
  const readScope = `${chatId || ''}:${activeTopicId || ''}`
  useEffect(() => {
    latestImmediateReadAloudMessageRef.current = latestImmediateReadAloudMessage
    if (!readAloudAuto) return
    if (!latestImmediateReadAloudMessage) return
    if (pendingImmediateReadAloudScopeRef.current !== readScope) return
    pendingImmediateReadAloudScopeRef.current = null
    void speakMessageAloud({
      messageId: latestImmediateReadAloudMessage.id,
      body: latestImmediateReadAloudMessage.body,
      source: 'auto',
      speed: readAloudSpeed,
      voice: readAloudVoice,
    })
  }, [
    latestImmediateReadAloudMessage,
    readAloudAuto,
    readAloudSpeed,
    readAloudVoice,
    readScope,
  ])
  const [visibilityTick, setVisibilityTick] = useState(0)
  useEffect(() => {
    function bumpVisibilityTick() {
      if (document.visibilityState !== 'visible') return
      setVisibilityTick(Date.now())
    }
    bumpVisibilityTick()
    window.addEventListener('focus', bumpVisibilityTick)
    window.addEventListener('agentchat:notification-open', bumpVisibilityTick)
    document.addEventListener('visibilitychange', bumpVisibilityTick)
    return () => {
      window.removeEventListener('focus', bumpVisibilityTick)
      window.removeEventListener('agentchat:notification-open', bumpVisibilityTick)
      document.removeEventListener('visibilitychange', bumpVisibilityTick)
    }
  }, [])
  useEffect(() => {
    if (!chatId) return
    if (document.visibilityState !== 'visible') return
    const enteredScope = lastMarkedRef.current !== readScope
    const sawNewMessages = messageCount > lastMessageCountRef.current
    const sawNewLatestMessage = latestMessageId !== lastLatestMessageIdRef.current
    const hasUnread = (openUnreadCount ?? 0) > 0 || unreadMessageCount > 0
    const topicKnown = activeTopicId ? activeTopic !== null : true
    if (!shouldDispatchReadReceipt({
      activeTopicId,
      composerAutoFocus,
      enteredScope,
      hasUnread,
      isGroupRoot,
      markReadPending,
      messagesFetching,
      messagesLoaded: Boolean(messagesForFocusDecision),
      sawNewLatestMessage,
      sawNewMessages,
      topicKnown,
      topicsLoaded: topics !== undefined,
    })) return

    lastMarkedRef.current = readScope
    lastMessageCountRef.current = messageCount
    lastLatestMessageIdRef.current = latestMessageId
    if (!isGroupRoot) clearReadNotificationsForChat(chatId, activeTopicId, chat?.display_name ?? null)
    markChatRead({ topicId: activeTopicId, reconcileMessages: false })
  }, [
    activeTopicId,
    activeTopic,
    chatId,
    chat?.display_name,
    composerAutoFocus,
    isGroupRoot,
    latestMessageId,
    markChatRead,
    markReadPending,
    messageCount,
    messagesFetching,
    messagesForFocusDecision,
    notificationOpen,
    openUnreadCount,
    readScope,
    topics,
    unreadMessageCount,
    visibilityTick,
  ])
  const openTopic = useCallback((topicId: string, options?: { focusComposer?: boolean }) => {
    setActiveTopicId(topicId, { focusComposer: options?.focusComposer === true })
  }, [setActiveTopicId])
  const clearNewTopicShareParam = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.delete('new_topic_share')
      p.delete('new_topic_prompt')
      return p
    }, { replace: true })
  }, [setSearchParams])
  const openSearchHit = useCallback((hit: SearchHit) => {
    setThreadSearchOpen(false)
    setThreadSearchQuery('')
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.set('chat_id', hit.chat_id)
      p.delete('topic')
      p.delete('message')
      p.delete('notification_open')
      if (hit.topic_id) p.set('topic_id', hit.topic_id)
      else p.delete('topic_id')
      p.set('message_id', hit.message_id)
      return p
    }, { replace: true })
  }, [setSearchParams])

  const orderedTopicIdsForCurrentContext = useCallback((currentTopicId: string): string[] => {
    if (!chatId) return []
    const contextTopics = isCodeChatId(chatId)
      ? codeTopicsForContext(topics || [], currentTopicId)
      : topics || []
    const topicById = new Map(contextTopics.map((topic) => [topic.id, topic]))
    const context = loadTopicAdvanceContext(chatId)
    if (context?.orderedTopicIds.includes(currentTopicId)) {
      return context.orderedTopicIds.filter((topicId) => {
        if (topicId === currentTopicId) return true
        const topic = topicById.get(topicId)
        return topic ? topic.closed !== true : !isCodeChatId(chatId)
      })
    }

    return orderedTopicIdsForSection(contextTopics, currentTopicId)
  }, [chatId, topics])

  const nextTopicIdAfterActiveTopic = useCallback((currentTopicId: string): string | null => (
    nextTopicIdAfterCurrent(orderedTopicIdsForCurrentContext(currentTopicId), currentTopicId)
  ), [orderedTopicIdsForCurrentContext])

  const nextTopicIdAfterActiveDone = useCallback((currentTopicId: string): string | null => (
    nextTopicIdAfterRemovingCurrent(orderedTopicIdsForCurrentContext(currentTopicId), currentTopicId)
  ), [orderedTopicIdsForCurrentContext])

  const nextTopicId = activeTopicId ? nextTopicIdAfterActiveTopic(activeTopicId) : null
  const advanceToNextTopic = useCallback(() => {
    if (!nextTopicId) return
    setShowKebab(false)
    setActiveTopicId(nextTopicId, { replace: true })
  }, [nextTopicId, setActiveTopicId])

  const previousTopicId = activeTopicId
    ? previousTopicIdBeforeCurrent(orderedTopicIdsForCurrentContext(activeTopicId), activeTopicId)
    : null
  const goToPreviousTopic = useCallback(() => {
    if (!previousTopicId) return
    setShowKebab(false)
    setActiveTopicId(previousTopicId, { replace: true })
  }, [previousTopicId, setActiveTopicId])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      if (chat && chat.type !== 'auto-spawn' && !activeTopicId && chatId) {
        const target = adjacentLaneId(chatId, event.key === 'ArrowLeft' ? -1 : 1,
          (allChats ?? []).filter((lane) => lane.type !== 'auto-spawn').map((lane) => lane.id))
        const openLane = target ? () => navigate(chatTarget(target), { replace: true }) : null
        handleChatNavigationShortcut(event, backButtonRef.current, openLane, openLane)
      } else {
        handleChatNavigationShortcut(event, nextTopicButtonRef.current,
          previousTopicId ? goToPreviousTopic : null, () => nextTopicButtonRef.current?.click())
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [previousTopicId, goToPreviousTopic, chat, activeTopicId, chatId, allChats, navigate])

  const closeActiveTopicAndAdvance = useCallback(async (closeTodoistTask: boolean) => {
    if (!activeTopicId) return
    if (closeTodoistTask && isTodoistSourceKind(activeTopic?.source_kind)) {
      const confirmed = await confirmTodoistClose({ topicIds: [activeTopicId] })
      if (!confirmed) return
    }
    const nextTopicId = nextTopicIdAfterActiveDone(activeTopicId)
    await closeTopic.mutateAsync({ topic_id: activeTopicId, closeTodoist: closeTodoistTask })
    setShowKebab(false)
    setActiveTopicId(nextTopicId, { replace: true })
  }, [activeTopic, activeTopicId, closeTopic, nextTopicIdAfterActiveDone, setActiveTopicId])

  const markActiveTopicDone = useCallback(async () => {
    if (commitmentActionStripVisible(activeTopic?.source_kind)) return
    setBackgroundTopicActionError(null)
    try {
      await closeActiveTopicAndAdvance(true)
    } catch (error) {
      setBackgroundTopicActionError(error instanceof Error
        ? error.message
        : 'Could not complete the Todoist task. Nothing was closed.')
    }
  }, [activeTopic?.source_kind, closeActiveTopicAndAdvance])

  const f8Shortcut = useMemo(createF8Shortcut, [])
  useEffect(() => {
    window.addEventListener('blur', f8Shortcut.cancel)
    return () => {
      f8Shortcut.cancel()
      window.removeEventListener('blur', f8Shortcut.cancel)
    }
  }, [f8Shortcut, chatId, activeTopicId])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'F8') return
      const route = chatTargetFromUrl(new URL(window.location.href))
      if (!route || route.chatId !== chatId || route.topicId !== activeTopicId) return
      const isLane = chat && chat.type !== 'auto-spawn' && !activeTopicId
      const nextLane = isLane && chatId ? adjacentLaneId(chatId, 1,
        (allChats ?? []).filter((lane) => lane.type !== 'auto-spawn').map((lane) => lane.id)) : null
      const openNext = isLane
        ? nextLane ? () => navigate(chatTarget(nextLane), { replace: true }) : null
        : nextTopicId ? advanceToNextTopic : null
      const complete = isLane
        ? reset.isPending ? null : () => f8ActionButtonRef.current?.click()
        : activeTopic && !activeTopic.closed && !closeTopic.isPending ? () => {
          if (f8ActionButtonRef.current) f8ActionButtonRef.current.click()
          else void markActiveTopicDone()
        } : null
      f8Shortcut.handle(event, backButtonRef.current, openNext, complete)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [f8Shortcut, chat, chatId, activeTopicId, activeTopic, allChats, navigate,
    nextTopicId, advanceToNextTopic, reset, closeTopic.isPending, markActiveTopicDone])

  const advanceAfterExternalTopicClose = useCallback((
    closedTopicId: string,
    nextTopicId: string | null,
  ) => {
    if (activeTopicId !== closedTopicId) return
    setActiveTopicId(nextTopicId, { replace: true })
  }, [activeTopicId, setActiveTopicId])

  /* When ChatPage mounts (or chatId changes) tell the service worker to
     close any OS notifications still hanging around for this chat. The
     user is now reading these messages in the open chat/topic — the
     notifications have done their job. The group root is excluded because
     topic notifications are only read after opening that topic. */
  useEffect(() => {
    if (!chatId) return
    if (isGroupRoot) return
    clearReadNotificationsForChat(chatId, activeTopicId, chat?.display_name ?? null)
  }, [activeTopicId, chat?.display_name, chatId, isGroupRoot, visibilityTick])

  // Robust click-away + Escape close for the kebab menu. Uses pointerdown
  // (works on touch + mouse) and checks the ref tree to skip clicks INSIDE
  // the menu itself.
  useEffect(() => {
    if (!showKebab) return
    function onPointer(e: PointerEvent) {
      const el = kebabRef.current
      if (el && !el.contains(e.target as Node)) {
        setShowKebab(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowKebab(false)
    }
    document.addEventListener('pointerdown', onPointer, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [showKebab])

  useEffect(() => {
    if (!showKebab) setShowFreeLaneMenu(false)
  }, [showKebab])

  if (!chatId) return null
  // WhatsApp-grade: only the FIRST EVER load of this chat blocks on
  // the spinner. ``useChat`` has placeholderData from the chats list
  // cache + 7-day localStorage persistence, so subsequent visits get
  // ``chat`` immediately and skip this branch.
  if (isLoading && !chat) {
    return (
      <div className="p-6 text-stone-500 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-stone-400 animate-pulse" />
        Loading chat…
      </div>
    )
  }
  // Same reliability rule as ChatListPage: NEVER nuke the page on a
  // transient backend hiccup if we have any cached chat to fall back
  // on. Only show the full-page error when there's truly nothing to
  // render (first-ever visit + the backend is unreachable). The amber
  // "Reconnecting" pill renders inside the header below to tell Mark
  // his data is stale without blocking him.
  if (!chat) {
    const offline = typeof navigator !== 'undefined' && !navigator.onLine
    return (
      <div className="p-6 text-red-600">
        {offline ? 'Chat is not cached on this device yet.' : 'Chat not found.'}
      </div>
    )
  }

  // Topic-aware header: when Mark is INSIDE a topic in an auto-spawn
  // group (Email / Todoist Tasks / X Links / Freelancer / Health Checks),
  // the title + timestamps in the header
  // should reflect the TOPIC, not the group. Otherwise the header is
  // identical for every topic in the group, which Mark called out as
  // useless. Falls back to chat-level data when no topic is active or
  // for user-driven (single-topic) chats.
  // Status badge: per-topic when viewing a topic, lane-level otherwise.
  // Mark's rule: "[the badge] shouldnt care about other topics in the
  // group only its own". So a topic in pwa-auto-x-links shows green-idle
  // even while 4 sibling topics are mid-verdict on parallel subprocesses,
  // because THIS topic itself isn't doing anything. The lane-level
  // fallback applies to manual chats (Codex/Claude #1-5) and to
  // auto-spawn chats viewed at the group level (no topic selected).
  const status = activeTopic
    ? topicStatusFriendly(activeTopic.status, {
        startedAt: activeTopic.last_message_ts,
        lastProgressAt: activeTopic.last_progress_at,
        lastProgressKind: activeTopic.last_progress_kind,
      })
    : laneStatusFriendly(chat.lane_status, chat.lane_last_heartbeat, {
        busySince: chat.lane_busy_since,
        lastProgressAt: chat.last_progress_at,
        lastProgressKind: chat.last_progress_kind,
      }, wsConnected)

  const headerTitle = activeTopic
    ? topicDisplayTitle(activeTopic)
    : chatDisplayTitle(chat)
  const activeTopicLifecycle = activeTopic ? topicLifecycle(activeTopic) : null

  // "Codex 2 · failover" / "Codex 1 · pinned": which login the NEXT Codex
  // turn runs on when it differs from the plain Codex 1 default. Resolved
  // from the topic's own model first, like the badge.
  const codexAccountHeaderChip = codexAccountChip(
    codeComposerAgent(chatId, activeTopic
      ? (activeTopic.preferred_model || chat.preferred_model || chat.agent)
      : (chat.preferred_model || chat.agent)),
    codexAvailability,
  )
  const headerEngagedTopic = effectiveWidth >= 1024 && activeTopic && activeTopic.closed !== true
    ? engagedHeaderTopics?.find((topic) => topic.chat_id === chatId && topic.id === activeTopicId)
    : undefined
  const activeTopicHasCommitmentActions = commitmentActionStripVisible(activeTopic?.source_kind)
  const showTopicHeaderNav = !!activeTopicId && !!activeTopic && activeTopic.closed !== true
  const showTopicDoneHeaderButton = !headerEngagedTopic && showTopicHeaderNav && !activeTopicHasCommitmentActions && !isTodoistSourceKind(activeTopic?.source_kind)
  const showLaneResetButton = !activeTopicId && chat.type !== 'auto-spawn'
  const showReachableActions = effectiveWidth >= 560 && (showTopicHeaderNav || showLaneResetButton)
  const activeTopicSourceKind = activeTopic?.source_kind ?? null
  const showCommitmentAddPill = chatUsesCommitmentAddPill(chatId) && !activeTopicId
  const showTodoistSourcePill = topicUsesTodoistSourcePill(activeTopicSourceKind)
  const showEmailSourcePill = topicUsesEmailSourcePill(activeTopicSourceKind)
  const showCommitmentReviewPill = topicUsesCommitmentReviewPill(activeTopicSourceKind)
  const showEventTicketPill = topicUsesEventTicketPill(activeTopicSourceKind)
  const showTweetPreviewCard =
    !!activeTopicId
    && tweetPreviewOpen
    && topicUsesTweetPreviewCard(activeTopicSourceKind)

  // Uptime resets to 0 the moment Mark hits Reset — last_reset_at takes
  // precedence over created_at when present. For topics, the topic's
  // created_at IS the reset point (topics don't have a separate reset
  // semantic — opening a new topic = fresh state).
  const uptimeAnchor = activeTopic
    ? activeTopic.created_at
    : Math.max(chat.created_at, chat.last_reset_at || 0)
  const uptime = relativeUptime(uptimeAnchor)

  // "replied N ago" — for topics, the topic's last agent message; for
  // chats, the chat-level field already SQL-filtered against last_reset_at.
  const lastReplyTs = activeTopic
    ? (activeTopic.last_message_direction === 'agent'
        || activeTopic.last_message_direction === 'outbound'
       ? activeTopic.last_message_ts
       : null)
    : chat.last_agent_reply_at
  const composerAgent = codeComposerAgent(chatId, (
    activeTopic?.preferred_model || chat.display_agent || chat.preferred_model || chat.agent
  ) as Agent)
  return (
    <ReachableActions enabled={showReachableActions}>
      <header data-chat-id={chatId} data-topic-id={activeTopicId ?? ''} className="relative z-50 flex items-center gap-2 px-3 py-1.5 border-b border-stone-200 dark:border-stone-800 bg-stone-50/95 dark:bg-stone-900/95 backdrop-blur">
        {/* Back: hierarchical up-navigation via backTarget() — the exact
            same resolver the Android hardware back uses, so this arrow and the
            system back button always do the same thing regardless of how Mark
            got here. Never exits the app. */}
        <button
          ref={backButtonRef}
          onClick={goUp}
          className="grid place-items-center w-9 h-9 rounded-lg hover:bg-stone-200 dark:hover:bg-stone-800 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        {/* When a topic is open, show the topic's actual model — a topic
            can run on a different agent than its chat's default (e.g. an
            opportunistic architecture-deepen topic pinned to codex). Fall
            back to the group's current display agent at the group root. */}
        <AgentBadge
          agent={
            codeChatAgent(chatId) === 'gemini'
              ? 'gemini'
              : activeTopic
                ? (activeTopic.preferred_model || chat.agent)
                : chat.uses_multiple_agents
                ? 'mixed'
                : (chat.display_agent || chat.preferred_model || chat.agent)
          }
          size={24}
          laneId={chat.lane_id}
          sessionRef={activeTopic?.session_ref}
        />
        {headerSearchOpen ? (
          <input
            autoFocus
            type="text"
            enterKeyHint="search"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck={false}
            placeholder={groupSearchOpen
              ? 'Search topics…'
              : activeTopicId
                ? 'Search this topic…'
                : 'Search this chat…'}
            value={groupSearchOpen ? groupSearchQuery : threadSearchQuery}
            onChange={(e) => {
              if (groupSearchOpen) setGroupSearchQuery(e.target.value)
              else setThreadSearchQuery(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return
              if (groupSearchOpen) {
                setGroupSearchOpen(false)
                setGroupSearchQuery('')
              } else {
                setThreadSearchOpen(false)
                setThreadSearchQuery('')
              }
            }}
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-stone-400 dark:placeholder:text-stone-500"
          />
        ) : (
        <div className="min-w-0 flex-1 flex flex-col justify-center">
          {activeTopic ? (
              <button
                type="button"
                onClick={() => {
                  if (isTodoistSourceKind(activeTopic.source_kind)) startTopicRename()
                  else setTopicDetailsOpen(true)
                }}
                className="min-w-0 max-w-full truncate text-left font-semibold leading-tight"
                title={headerTitle}
                aria-label={isTodoistSourceKind(activeTopic.source_kind) ? 'Rename Todoist task' : 'Show topic details'}
                data-testid="topic-header-title-button"
              >
                {headerTitle}
              </button>
          ) : renamingChat ? (
            <input
              autoFocus
              value={chatRenameDraft}
              placeholder={chat.display_name || chat.id}
              onChange={(e) => setChatRenameDraft(e.target.value)}
              onBlur={() => {
                if (skipChatRenameBlurRef.current) {
                  skipChatRenameBlurRef.current = false
                  return
                }
                submitChatRename()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitChatRename()
                if (e.key === 'Escape') {
                  skipChatRenameBlurRef.current = true
                  setRenamingChat(false)
                }
              }}
              className="flex-1 min-w-0 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-600 rounded px-2 py-0.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <span
              className="min-w-0 max-w-full truncate text-left font-semibold leading-tight"
              title={chat.session_title && chat.display_name ? `${chat.session_title} (${chat.display_name})` : headerTitle}
            >
              {headerTitle}
            </span>
          )}
          {/* min-w-0/overflow-hidden: with an account chip the status row can
              outgrow the title column on a phone; clip the trailing uptime
              text instead of painting under the header buttons. */}
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
            {chat.pinned && <Pin className="w-3 h-3 text-amber-500 shrink-0" aria-label="Pinned" />}
            {chatError && (
              <span
                className="text-[9px] font-medium px-1.5 py-0.5 rounded-full
                           bg-amber-100 text-amber-800 border border-amber-300
                           inline-flex items-center gap-0.5 shrink-0"
                title={`Reconnecting — ${String((chatError as Error).message ?? '').slice(0, 200)}`}
              >
                <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" />
                Reconnecting
              </span>
            )}
            <span
              className="text-[11px] text-stone-500 flex items-center gap-1 min-w-0 overflow-hidden whitespace-nowrap"
              title={status.description}
            >
              <span
                className={clsx(
                  'inline-block w-1.5 h-1.5 rounded-full',
                  TONE_BG[status.tone],
                  status.blink && 'animate-pulse',
                )}
              />
              {status.label && <span>{status.label}</span>}
              {activeTopicLifecycle && (
                <span
                  className={clsx(
                    'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                    TOPIC_LIFECYCLE_CHIP_CLASSES[activeTopicLifecycle.tone],
                  )}
                  title={activeTopicLifecycle.description}
                  data-testid="topic-lifecycle-chip"
                >
                  {activeTopicLifecycle.label}
                </span>
              )}
              {codexAccountHeaderChip && (
                <span
                  className={clsx(
                    'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-bold tracking-wide whitespace-nowrap',
                    CODEX_ACCOUNT_CHIP_CLASSES[codexAccountHeaderChip.tone],
                  )}
                  title={codexAccountHeaderChip.description}
                  data-testid="codex-account-chip"
                >
                  {codexAccountHeaderChip.label}
                </span>
              )}
              {/* No leading dot straight after the status dot when there is no label. */}
              {uptime && (
                <span>
                  {status.label || activeTopicLifecycle || codexAccountHeaderChip ? '· ' : ''}{activeTopic ? 'opened' : 'up'} {uptime}
                </span>
              )}
              {lastReplyTs && (
                <span>
                  {status.label || activeTopicLifecycle || codexAccountHeaderChip || uptime ? '· ' : ''}{relativeLastReply(lastReplyTs)}
                </span>
              )}
            </span>
          </div>
        </div>
        )}

        {showFullWindowToggle && !headerSearchOpen && (
          <button
            onClick={toggleFullWindow}
            className={clsx(
              'inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors',
              chatFullWindow
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:hover:bg-blue-900'
                : 'hover:bg-stone-200 dark:hover:bg-stone-800',
            )}
            title={chatFullWindow ? 'Show split view' : 'Use full window'}
            aria-label={chatFullWindow ? 'Show split view' : 'Use full window'}
            aria-pressed={chatFullWindow}
          >
            {chatFullWindow ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            <span>{chatFullWindow ? 'Split' : 'Full'}</span>
          </button>
        )}
        {headerEngagedTopic && !headerSearchOpen && <EngagedTopicDoneButton topic={headerEngagedTopic} />}
        <div className="flex shrink-0 items-center gap-0.5">
        {headerSearchOpen && (
          <button
            type="button"
            onClick={() => {
              setGroupSearchOpen(false)
              setGroupSearchQuery('')
              setThreadSearchOpen(false)
              setThreadSearchQuery('')
            }}
            className="grid place-items-center w-9 h-9 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
            title="Close search"
            aria-label="Close search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {!headerSearchOpen && (
          <>
        {showTopicHeaderNav ? (
          <>
            {!isCodeChatId(chatId) && !activeTopicHasCommitmentActions && <button
              ref={nextTopicButtonRef}
              type="button"
              onClick={advanceToNextTopic}
              disabled={!nextTopicId}
              className={clsx(
                'inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'bg-blue-600 text-white hover:bg-blue-700',
                'dark:bg-blue-500 dark:text-blue-950 dark:hover:bg-blue-400',
              )}
              title={nextTopicId ? 'Open next topic without marking this one done (F8 or → next, ← previous on desktop)' : 'No next topic in this list'}
              aria-keyshortcuts="ArrowRight F8"
              aria-label="Open next topic without marking this one done"
              data-testid="topic-next-button"
            >
              <ArrowRight className="w-4 h-4" />
              <span>Next</span>
            </button>}
            {showTopicDoneHeaderButton && (
              <ReachableAction>
                <button
                  ref={f8ActionButtonRef}
                  type="button"
                  onClick={markActiveTopicDone}
                  disabled={closeTopic.isPending}
                  aria-busy={closeTopic.isPending}
                  className={clsx(
                    'inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors',
                    'disabled:cursor-wait disabled:opacity-60',
                    'bg-emerald-600 text-white hover:bg-emerald-700',
                    'dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400',
                  )}
                  title="Mark this topic done and open the next topic (double-press F8 on desktop)"
                  aria-label="Mark topic done and open next topic"
                >
                  {closeTopic.isPending
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <CircleCheck className="w-4 h-4" />}
                  <span>Done</span>
                </button>
              </ReachableAction>
            )}
          </>
        ) : null}
        {/* Reset chat — regular lanes only; group roots and topics have no agent process to reset. */}
        {!activeTopicId && chat.type !== 'auto-spawn' && (
          <ReachableAction>
            <button
              ref={f8ActionButtonRef}
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
              className="grid place-items-center w-9 h-9 rounded-lg hover:bg-stone-200 dark:hover:bg-stone-800 transition-colors text-stone-600 dark:text-stone-300 disabled:opacity-50"
              title="Reset this chat — clears the queue and restarts the agent process. Double-press F8 on desktop."
              aria-label="Reset chat"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </ReachableAction>
        )}
        {!activeTopicId && chat.type !== 'auto-spawn' && (
          <button
            onClick={() => setShowLaneSnoozePicker(true)}
            disabled={snoozeChat.isPending}
            className={clsx(
              'grid place-items-center w-9 h-9 rounded-lg transition-colors disabled:opacity-50',
              chat.is_snoozed
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                : 'text-stone-600 hover:bg-stone-200 dark:text-stone-300 dark:hover:bg-stone-800',
            )}
            title={chat.is_snoozed ? 'Reschedule this lane snooze' : 'Snooze this lane'}
            aria-label={chat.is_snoozed ? 'Reschedule lane snooze' : 'Snooze lane'}
          >
            <MoonStar className="w-4 h-4" />
          </button>
        )}
        {!headerEngagedTopic && activeTopicId && activeTopic && activeTopic.closed !== true && !activeTopicHasCommitmentActions && (
          <button
            type="button"
            onClick={() => setShowSnoozePicker(true)}
            disabled={snoozeTopic.isPending}
            className={clsx(
              'grid shrink-0 place-items-center w-9 h-9 rounded-lg transition-colors disabled:opacity-50',
              activeTopic.is_snoozed
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                : 'text-stone-600 hover:bg-stone-200 dark:text-stone-300 dark:hover:bg-stone-800',
            )}
            title={activeTopic.is_snoozed ? 'Reschedule this topic snooze' : 'Snooze this topic'}
            aria-label={activeTopic.is_snoozed ? 'Reschedule topic snooze' : 'Snooze topic'}
            data-testid="topic-snooze-button"
          >
            <MoonStar className="w-4 h-4" />
          </button>
        )}
        <div className="relative" ref={kebabRef}>
          <button
            ref={kebabButtonRef}
            onClick={() => setShowKebab((v) => !v)}
            className="grid place-items-center w-9 h-9 rounded-lg hover:bg-stone-200 dark:hover:bg-stone-800 transition-colors"
            aria-label="More actions"
            aria-expanded={showKebab}
            aria-haspopup="menu"
            title="More"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showKebab && (
            <div className="fixed inset-0 z-40" aria-hidden onClick={() => setShowKebab(false)} />
          )}
          {/* Keep Share mounted so closing the menu cannot dismiss its sheet. */}
          <ul
            hidden={!showKebab}
            role="menu"
            className="absolute right-0 top-full mt-1 z-50 max-h-[calc(100dvh-6rem)] min-w-[240px] overflow-y-auto bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl shadow-2xl p-1.5">
                <KebabItem
                  onClick={() => {
                    setShowKebab(false)
                    setGroupSearchQuery('')
                    setThreadSearchQuery('')
                    setGroupSearchOpen(isGroupRoot)
                    setThreadSearchOpen(!isGroupRoot)
                  }}
                  icon={<Search className="w-3.5 h-3.5" />}
                >
                  {isGroupRoot ? 'Search topics' : activeTopicId ? 'Search this topic' : 'Search this chat'}
                </KebabItem>
                {!showTopicHeaderNav && (
                  <>
                    <KebabItem
                      onClick={() => setShowFreeLaneMenu((open) => !open)}
                      icon={<List className="w-3.5 h-3.5" />}
                      expanded={showFreeLaneMenu}
                    >
                      Free lanes
                    </KebabItem>
                    {showFreeLaneMenu && (
                      <li role="none">
                        <ul role="menu" aria-label="Free lanes" className="ml-3 border-l border-stone-200 pl-1 dark:border-stone-700">
                          {freeLaneTargets.length === 0 ? (
                            <li className="px-2.5 py-2 text-sm text-stone-500 dark:text-stone-400">No free lanes right now</li>
                          ) : freeLaneTargets.map((target) => {
                            return (
                              <li key={target.id}>
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setShowKebab(false)
                                    setShowFreeLaneMenu(false)
                                    leaveChatRoute(chatListTarget(target))
                                  }}
                                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-stone-700 transition-colors hover:bg-stone-100 dark:text-stone-200 dark:hover:bg-stone-700"
                                >
                                  <AgentBadge agent={target.agent} size={18} laneId={target.lane_id} />
                                  <span className="min-w-0 flex-1 truncate">{chatDisplayTitle(target)}</span>
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      </li>
                    )}
                  </>
                )}
                {canUseLiveVoice && voiceAvailable && (
                  <KebabItem
                    onClick={() => { setLiveVoiceScope(voiceScope); setShowKebab(false) }}
                    icon={<Mic className="w-3.5 h-3.5" />}
                  >
                    Live voice · trial
                  </KebabItem>
                )}
                {!isGroupRoot && (
                  <GuestShareButton
                    key={`${chatId}:${activeTopicId ?? 'root'}`}
                    chatId={chatId}
                    topicId={activeTopicId}
                    title={headerTitle}
                    onOpen={() => setShowKebab(false)}
                    returnFocusRef={kebabButtonRef}
                  />
                )}
                <KebabItem
                  onClick={() => { setShowKebab(false); setAskingAnother(true) }}
                  icon={<MessageSquarePlus className="w-3.5 h-3.5" />}
                >
                  Get a second opinion
                </KebabItem>
                {!isGroupRoot && (
                  <KebabItem
                    onClick={() => { toggleReadAloudAuto(); setShowKebab(false) }}
                    icon={readAloudAuto
                      ? <Volume2 className="w-3.5 h-3.5" />
                      : <VolumeX className="w-3.5 h-3.5" />}
                  >
                    {readAloudAuto ? 'Turn off auto-read replies' : 'Auto-read replies'}
                  </KebabItem>
                )}
                {!isGroupRoot && (
                  <KebabItem
                    onClick={() => { toggleReadAloudAutoListen(); setShowKebab(false) }}
                    icon={<Mic className="w-3.5 h-3.5" />}
                  >
                    {readAloudAutoListen ? 'Turn off auto-listen after read' : 'Auto-listen after read'}
                  </KebabItem>
                )}
                {activeTopicId && activeTopic && (
                  <KebabItem
                    onClick={() => { setShowKebab(false); startTopicRename() }}
                    icon={<Edit2 className="w-3.5 h-3.5" />}
                  >
                    Rename topic
                  </KebabItem>
                )}
                {!activeTopicId && chat.type !== 'auto-spawn' && (
                  <KebabItem
                    onClick={() => {
                      setShowKebab(false)
                      setChatRenameDraft(chat.session_title || '')
                      setRenamingChat(true)
                    }}
                    icon={<Edit2 className="w-3.5 h-3.5" />}
                  >
                    Edit lane name
                  </KebabItem>
                )}
                {activeTopicId && activeTopic && activeTopic.closed !== true && !activeTopic.is_snoozed && !activeTopicHasCommitmentActions && (
                  <>
                    <KebabItem
                      disabled={closeTopic.isPending}
                      onClick={markActiveTopicDone}
                      icon={<CircleCheck className="w-3.5 h-3.5" />}
                    >
                      Mark topic done
                    </KebabItem>
                  </>
                )}
                {activeTopicId && activeTopic && activeTopic.closed !== true && activeTopic.is_snoozed && (
                  <>
                    <KebabItem
                      disabled={snoozeTopic.isPending}
                      onClick={async () => {
                        setShowKebab(false)
                        await snoozeTopic.mutateAsync({
                          topic_id: activeTopicId,
                          wake_at: Math.floor(Date.now() / 1000) + 1,
                        }).catch(() => {})
                      }}
                      icon={<BellOff className="w-3.5 h-3.5" />}
                    >
                      Wake now
                    </KebabItem>
                  </>
                )}
                {activeTopicId && activeTopic?.closed === true && (
                  <KebabItem
                    disabled={reopenTopic.isPending}
                    onClick={async () => {
                      setShowKebab(false)
                      await reopenTopic.mutateAsync({
                        topic_id: activeTopicId,
                        reopenTodoist: false,
                      }).catch(() => {})
                    }}
                    icon={<RotateCcw className="w-3.5 h-3.5" />}
                  >
                    Reopen topic
                  </KebabItem>
                )}
                <KebabItem
                  onClick={() => { pinChat.mutate(!chat.pinned); setShowKebab(false) }}
                  icon={<Pin className="w-3.5 h-3.5" />}
                >
                  {chat.pinned ? 'Unpin from top' : 'Pin to top of list'}
                </KebabItem>
                {!activeTopicId && chat.type !== 'auto-spawn' && chat.is_snoozed && (
                  <KebabItem
                    disabled={snoozeChat.isPending}
                    onClick={async () => {
                      setShowKebab(false)
                      await snoozeChat.mutateAsync({ wake_at: null }).catch(() => {})
                    }}
                    icon={<BellOff className="w-3.5 h-3.5" />}
                  >
                    Wake lane now
                  </KebabItem>
                )}

                {/* Per-chat mute. Suppresses OS push for this chat
                    (badge / unread state unaffected). Approvals bypass
                    server-side. Useful for noisy groups (X-Links) or
                    in-development groups Mark hasn't started using yet. */}
                <KebabItem
                  onClick={() => { muteChat.mutate(!chat.muted); setShowKebab(false) }}
                  icon={chat.muted
                    ? <BellOff className="w-3.5 h-3.5" />
                    : <Bell className="w-3.5 h-3.5" />}
                >
                  {chat.muted ? 'Unmute notifications' : 'Mute notifications'}
                </KebabItem>
                {chat.type === 'auto-spawn' && (
                  <KebabItem
                    onClick={() => {
                      muteTopicStart.mutate(!chat.mute_topic_start_notifications)
                      setShowKebab(false)
                    }}
                    icon={chat.mute_topic_start_notifications
                      ? <BellOff className="w-3.5 h-3.5" />
                      : <Bell className="w-3.5 h-3.5" />}
                  >
                    {chat.mute_topic_start_notifications
                      ? 'Notify on new-topic first replies'
                      : 'Mute new-topic first replies'}
                  </KebabItem>
                )}

                {isGroupRoot && (
                  <KebabItem
                    disabled={groupUnreadCount === 0 || markReadPending}
                    onClick={() => {
                      markChatRead({ allTopics: true })
                      clearReadNotificationsForChat(chat.id, null, chat.display_name ?? null)
                      setShowKebab(false)
                    }}
                    icon={<CheckCheck className="w-3.5 h-3.5" />}
                  >
                    Mark all read{groupUnreadCount > 0 ? ` (${groupUnreadCount})` : ''}
                  </KebabItem>
                )}

                {/* Per-group default-model selector: ONLY shown on auto-spawn
                    group chats (X-Links / Todoist / Email / etc.)
                    and ONLY affects NEW topics spawned in the group going
                    forward — existing topics keep the model they were created
                    with, so an X-link verdict mid-flight on Codex doesn't
                    suddenly resume on Claude.
                    Manual lanes (Codex/Claude #1-5) intentionally have NO
                    agent-switch UI: each lane is hard-bound to its agent at
                    spawn time. To talk to the other agent, use its dedicated
                    lane. (Removed Sept 2026 per Mark's call: switching at the
                    individual-chat level was always going to confuse the
                    persistent-session model — the switch could only ever be
                    "on this chat's NEW work", which is exactly what topics
                    already give you in the group case.) */}
                {chat.type === 'auto-spawn' && chat.category !== 'architecture-review' && chat.agent !== 'gemini' && (
                  <>
                    {WRITABLE_MODEL_OPTIONS.map((option) => {
                      const currentModel = (chat.preferred_model || chat.agent) as WritableModel
                      const selected = currentModel === option.value
                      return (
                        <KebabItem
                          key={option.value}
                          disabled={false}
                          onClick={async () => {
                            setShowKebab(false)
                            if (option.value === 'claude') {
                              setClaudeEffortDialog({
                                chatName: chatDisplayTitle(chat),
                                initialEffort: normalizeClaudeEffort(chat.claude_effort),
                              })
                              return
                            }
                            const initialCodexModel = normalizeCodexModel(chat.codex_model, option.value)
                            setCodexEffortDialog({
                              chatName: chatDisplayTitle(chat),
                              model: option.value,
                              initialCodexModel,
                              initialEffort: normalizeCodexEffort(chat.codex_effort, initialCodexModel),
                            })
                          }}
                          icon={<Cpu className="w-3.5 h-3.5" />}
                        >
                          {selected
                            ? `Default model: ${option.label}`
                            : `Set default model: ${option.label}`}
                        </KebabItem>
                      )
                    })}
                  </>
                )}
                {chat.todoist_task_id && (
                  <>
                    <KebabItem
                      onClick={async () => {
                        setShowKebab(false)
                        setBackgroundTopicActionError(null)
                        try {
                          const confirmed = await confirmTodoistClose({
                            taskIds: [chat.todoist_task_id!],
                          })
                          if (!confirmed) return
                          await closeTodoist.mutateAsync()
                        } catch (error) {
                          setBackgroundTopicActionError(error instanceof Error
                            ? error.message
                            : 'Could not complete the Todoist task. Nothing was closed.')
                        }
                      }}
                      icon={<CheckSquare className="w-3.5 h-3.5" />}
                    >
                      Close source Todoist task
                    </KebabItem>
                    <KebabItem
                      href={`https://todoist.com/showTask?id=${chat.todoist_task_id}`}
                      icon={<ExternalLink className="w-3.5 h-3.5" />}
                    >
                      Open in Todoist
                    </KebabItem>
                  </>
                )}
          </ul>
        </div>
        </>
        )}
        </div>
      </header>

      {(backgroundTopicActionError || snoozeTopic.error || updateTopic.error || pinChat.error) && (
        <div
          role="alert"
          className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/70 dark:text-red-200"
        >
          <span className="min-w-0 flex-1 break-words">
            {(backgroundTopicActionError || updateTopic.error?.message || snoozeTopic.error?.message || pinChat.error?.message || 'Action failed').slice(0, 240)}
          </span>
          <button
            type="button"
            onClick={() => { setBackgroundTopicActionError(null); snoozeTopic.reset(); updateTopic.reset(); pinChat.reset() }}
            className="grid h-6 w-6 shrink-0 place-items-center rounded hover:bg-red-100 dark:hover:bg-red-900"
            aria-label="Dismiss background action error"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {threadSearchOpen && chatId && !isGroupRoot && (
        <ThreadSearchResults
          chat={chat}
          chatId={chatId}
          topicId={activeTopicId}
          query={threadSearchQuery}
          currentMessages={messagesForFocusDecision}
          scopeDone={!!activeTopic?.closed || !!chat.archived}
          onPick={openSearchHit}
        />
      )}

      {showSnoozePicker && activeTopicId && (
        <SnoozePicker
          onCancel={() => setShowSnoozePicker(false)}
          onPick={(wakeAtUnix) => {
            setShowSnoozePicker(false)
            snoozeTopic.mutate({
              topic_id: activeTopicId,
              wake_at: wakeAtUnix,
            })
          }}
        />
      )}

      {showLaneSnoozePicker && !activeTopicId && chat.type !== 'auto-spawn' && (
        <SnoozePicker
          title={chat.is_snoozed ? 'Reschedule lane' : 'Snooze lane'}
          presets={LANE_SNOOZE_PRESETS}
          footerNote="Hides this lane from the normal lane list until the chosen time. It stays available under the Snoozed filter."
          onCancel={() => setShowLaneSnoozePicker(false)}
          onPick={(wakeAtUnix) => {
            setShowLaneSnoozePicker(false)
            snoozeChat.mutate({
              wake_at: wakeAtUnix,
              reason: 'manual lane snooze',
            })
          }}
        />
      )}

      {/* Commitment-tracker quick-add — visible in the commitments group and threads.
          Renders nothing unless chatId === pwa-auto-commitments. */}
      {showCommitmentAddPill && (
        <Suspense fallback={null}>
          <CommitmentAddPill chatId={chatId} />
        </Suspense>
      )}

      {/* Round 3: groups (auto-spawn chats) render the topic LIST instead
          of the message stream when no topic is selected via ?topic=. The
          existing TopicTabs / MessageList / Composer triplet still applies
          for user-driven chats and for auto-spawn chats viewing a single
          topic. */}
      {redirectToCodeTab ? null : chat.type === 'auto-spawn' && !activeTopicId ? (
        <GroupListView
          chat={chat}
          onOpenTopic={openTopic}
          searchQuery={groupSearchQuery}
          searchOpen={groupSearchOpen}
          newTopicShareId={chat.id === X_LINKS_CHAT_ID ? newTopicShareId : null}
          newTopicPrompt={chat.id === X_LINKS_CHAT_ID ? newTopicPrompt : ''}
          onNewTopicShareConsumed={clearNewTopicShareParam}
        />
      ) : isCodeChatId(chatId) && activeTopicId ? (
        <Suspense fallback={<div className="p-4 text-sm">Opening conversation…</div>}>
          <CodeConversation key={`${chatId}:${activeTopicId}`} chatId={chatId} topicId={activeTopicId} topic={activeTopic}
            closed={!!activeTopic?.closed || !!chat.archived} readOnly={!!chat.archived} autoFocus={!!composerAutoFocus} chatAgent={composerAgent} />
        </Suspense>
      ) : (
        <>
          <Suspense fallback={null}>
            {!(chat.type === 'auto-spawn' && activeTopicId) && (
              <TopicTabs chatId={chatId} activeTopicId={activeTopicId} onSelect={setActiveTopicId} />
            )}
            {showTodoistSourcePill && (
              <TodoistSourcePill
                topicId={activeTopicId}
                tweetPreviewOpen={tweetPreviewOpen}
                onToggleTweetPreview={toggleTweetPreview}
                nextTopicIdAfterClose={nextTopicIdAfterActiveDone}
                onClosed={advanceAfterExternalTopicClose}
                onBackgroundError={setBackgroundTopicActionError}
                onSnooze={() => setShowSnoozePicker(true)}
              />
            )}
            {showEmailSourcePill && (
              <EmailSourcePill
                topicId={activeTopicId}
                nextTopicIdAfterClose={nextTopicIdAfterActiveDone}
                onClosed={advanceAfterExternalTopicClose}
              />
            )}
            {showCommitmentReviewPill && (
              <CommitmentReviewPill
                topicId={activeTopicId}
                nextTopicIdAfterResolve={nextTopicIdAfterActiveDone}
                onResolved={advanceAfterExternalTopicClose}
              />
            )}
            {showEventTicketPill && (
              <EventTicketPill
                topicId={activeTopicId}
                nextTopicIdAfterResolve={nextTopicIdAfterActiveDone}
                onResolved={advanceAfterExternalTopicClose}
              />
            )}
          </Suspense>
          {/* Tweet preview gets its own Suspense so its first-tap chunk
              load can't take down the source pill above it. */}
          <Suspense fallback={null}>
            {showTweetPreviewCard && activeTopicId && (
              <TweetPreviewCard topicId={activeTopicId} />
            )}
          </Suspense>

          <div className="flex-1 min-h-0 overflow-hidden chat-pattern-bg">
            <MessageList
              chatId={chatId}
              topicId={activeTopicId}
              targetMessageId={targetMessageId}
              aroundMessageId={aroundMessageId}
              notificationOpen={notificationOpen}
              notificationTargetMessageId={notificationTargetMessageId}
              chatType={chat.type}
              currentTopicTitle={activeTopic?.title ?? null}
              existingTopicTitles={topicTitles}
            />
          </div>
          {canUseLiveVoice && voiceAvailable && liveVoiceScope === voiceScope && (
            <Suspense fallback={null}>
              <LiveVoice key={voiceScope} chatId={chatId} topicId={activeTopicId} resetAt={chat.last_reset_at ?? 0} onClose={() => setLiveVoiceScope(null)} />
            </Suspense>
          )}
          <DoneSoFarPanel key={`progress:${chatId}:${activeTopicId ?? 'lane'}`} chatId={chatId} chat={chat} topic={activeTopic} />
          <SideJobsStrip key={`side-jobs:${chatId}:${activeTopicId ?? 'lane'}`} chatId={chatId} chat={chat} topic={activeTopic} />
          <StopTurnBar key={`${chatId}:${activeTopicId ?? 'lane'}`} chatId={chatId} chat={chat} topic={activeTopic} />
          <Composer
            chatId={chatId}
            topicId={activeTopicId}
            autoFocus={composerAutoFocus}
            autoVoiceReplyRequest={autoVoiceReplyRequest}
            chatAgent={composerAgent}
            enableClaudeEffort={composerAgent === 'claude'}
            chatType={chat.type}
            // A topic is its own conversation: its pickers open on what
            // that topic last ran, never on the lane root or a sibling.
            lastTurnRuntime={activeTopic ? activeTopic.last_turn_runtime : chat.last_turn_runtime}
            lastUsedRuntime={activeTopic ? null : chat.last_used_runtime}
          />
        </>
      )}

      {renamingTopic && activeTopic && (
        <dialog
          ref={topicRenameDialogRef}
          aria-labelledby="topic-rename-title"
          aria-modal="true"
          aria-busy={updateTopic.isPending}
          onCancel={(event) => {
            event.preventDefault()
            if (!updateTopic.isPending) setRenamingTopic(false)
          }}
          className="fixed inset-0 m-auto max-h-[calc(100dvh_-_2rem)] w-[calc(100vw_-_2rem)] max-w-xl overflow-y-auto rounded-2xl border border-stone-200 bg-white p-5 text-stone-900 shadow-2xl backdrop:bg-black/50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
        >
          <form onSubmit={(event) => { event.preventDefault(); submitTopicRename() }}>
            <h2 id="topic-rename-title" className="text-lg font-semibold">
              {isTodoistSourceKind(activeTopic.source_kind) ? 'Rename Todoist task' : 'Rename topic'}
            </h2>
            <label htmlFor="topic-rename-name" className="mt-4 block text-sm font-medium">
              {isTodoistSourceKind(activeTopic.source_kind) ? 'Task name' : 'Topic name'}
            </label>
            <textarea
              id="topic-rename-name"
              autoFocus
              value={renameDraft}
              onChange={(event) => setRenameDraft(event.target.value)}
              onKeyDown={(event) => {
                if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                  event.preventDefault()
                  submitTopicRename()
                }
              }}
              disabled={updateTopic.isPending}
              maxLength={500}
              rows={8}
              className="mt-2 min-h-48 max-h-[45dvh] w-full resize-y rounded-xl border border-stone-300 bg-white p-3 text-base leading-relaxed outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 dark:border-stone-600 dark:bg-stone-950"
            />
            <p className="mt-1 text-right text-xs text-stone-500 dark:text-stone-400">{renameDraft.length}/500</p>
            {updateTopic.error && (
              <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">
                {updateTopic.error.message}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRenamingTopic(false)}
                disabled={updateTopic.isPending}
                className="min-h-11 rounded-lg px-4 text-sm font-medium hover:bg-stone-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 disabled:opacity-50 dark:hover:bg-stone-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!renameDraft.trim() || updateTopic.isPending}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:opacity-50"
              >
                {updateTopic.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {updateTopic.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {topicDetailsOpen && activeTopic && (
        <TopicDetailsSheet
          chatName={chatDisplayTitle(chat)}
          topic={activeTopic}
          onClose={() => setTopicDetailsOpen(false)}
        />
      )}

      {claudeEffortDialog && (
        <ClaudeEffortDialog
          chatName={claudeEffortDialog.chatName}
          initialEffort={claudeEffortDialog.initialEffort}
          isPending={modelSwitchPending}
          onCancel={() => {
            if (!modelSwitchPending) setClaudeEffortDialog(null)
          }}
          onSubmit={async (effort) => {
            setModelSwitchPending(true)
            try {
              await updateGroupDefaultModel('claude', effort)
              setClaudeEffortDialog(null)
            } catch (err) {
              console.error('preferred_model PATCH failed', err)
            } finally {
              setModelSwitchPending(false)
            }
          }}
        />
      )}

      {codexEffortDialog && (
        <CodexEffortDialog
          chatName={codexEffortDialog.chatName}
          codexAgent={codexEffortDialog.model}
          initialModel={codexEffortDialog.initialCodexModel}
          initialEffort={codexEffortDialog.initialEffort}
          isPending={modelSwitchPending}
          onCancel={() => {
            if (!modelSwitchPending) setCodexEffortDialog(null)
          }}
          onSubmit={async (model, effort) => {
            setModelSwitchPending(true)
            try {
              await updateGroupDefaultModel(codexEffortDialog.model, undefined, model, effort)
              setCodexEffortDialog(null)
            } catch (err) {
              console.error('preferred_model PATCH failed', err)
            } finally {
              setModelSwitchPending(false)
            }
          }}
        />
      )}

      {askingAnother && (
        <AskAnotherModal
          onCancel={() => {
            setAskingAnother(false)
            setAskText('')
            setProHandoff(null)
          }}
          onSubmit={async (agent) => {
            const prompt = askText.trim()
            if (!prompt) return
            const result = await askAnother.mutateAsync({ agent, prompt, topic_id: activeTopicId })
            if (result.handoff) {
              setProHandoff(result.handoff)
              return
            }
            setAskingAnother(false)
            setAskText('')
          }}
          text={askText}
          setText={setAskText}
          isPending={askAnother.isPending}
          handoff={proHandoff}
          allowPro={chat.type === 'user-driven' || activeTopicId !== null}
        />
      )}
    </ReachableActions>
  )
}

function KebabItem({
  children, onClick, href, icon, destructive, disabled, expanded,
}: {
  children: React.ReactNode
  onClick?: () => void
  href?: string
  icon?: React.ReactNode
  destructive?: boolean
  disabled?: boolean
  expanded?: boolean
}) {
  const cls = clsx(
    'w-full text-left text-sm flex items-center gap-2.5 px-2.5 py-2 rounded-lg',
    'transition-colors',
    disabled
      ? 'text-stone-400 dark:text-stone-500 cursor-not-allowed'
      : destructive
        ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40'
        : 'text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700',
  )
  if (href) return <a href={href} className={cls}>{icon}{children}</a>
  return (
    <button onClick={onClick} className={cls} disabled={disabled} aria-expanded={expanded}>
      {icon}{children}
    </button>
  )
}

function ClaudeEffortDialog({
  chatName,
  initialEffort,
  onCancel,
  onSubmit,
  isPending,
}: {
  chatName: string
  initialEffort: ClaudeEffort
  onCancel: () => void
  onSubmit: (effort: ClaudeEffort) => void
  isPending: boolean
}) {
  const [picked, setPicked] = useState<ClaudeEffort>(initialEffort)
  const pickedLabel = CLAUDE_EFFORT_OPTIONS.find((option) => option.value === picked)?.label ?? 'High'

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl dark:bg-stone-800"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start gap-2">
          <Cpu className="mt-0.5 h-4 w-4 text-blue-600" aria-hidden />
          <div className="min-w-0">
            <h3 className="text-base font-semibold">Claude thinking</h3>
            <p className="mt-0.5 truncate text-xs text-stone-500">{chatName}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {CLAUDE_EFFORT_OPTIONS.map((option) => {
            const selected = option.value === picked
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setPicked(option.value)}
                className={clsx(
                  'h-10 rounded-lg border px-3 text-sm font-medium transition-colors',
                  selected
                    ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-200'
                    : 'border-stone-200 text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-200 dark:hover:bg-stone-700',
                )}
                aria-pressed={selected}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="h-9 rounded-lg px-3 text-sm font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50 dark:text-stone-300 dark:hover:bg-stone-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(picked)}
            disabled={isPending}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-wait disabled:bg-blue-300"
          >
            {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            Use {pickedLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function CodexEffortDialog({
  chatName,
  codexAgent,
  initialModel,
  initialEffort,
  onCancel,
  onSubmit,
  isPending,
}: {
  chatName: string
  codexAgent: WritableModel
  initialModel: CodexModel
  initialEffort: CodexEffort
  onCancel: () => void
  onSubmit: (model: CodexModel, effort: CodexEffort) => void
  isPending: boolean
}) {
  const [pickedModel, setPickedModel] = useState<CodexModel>(initialModel)
  const [picked, setPicked] = useState<CodexEffort>(initialEffort)
  const availableModels = codexModelOptionsForAgent(codexAgent)
  const availableEfforts = codexEffortsForModel(pickedModel)
  const pickedLabel = CODEX_EFFORT_OPTIONS.find((option) => option.value === picked)?.label ?? 'High'
  const pickedModelLabel = CODEX_MODEL_OPTIONS.find((option) => option.value === pickedModel)?.label ?? 'GPT-6.1 Sol'

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl dark:bg-stone-800"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start gap-2">
          <Cpu className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden />
          <div className="min-w-0">
            <h3 className="text-base font-semibold">Codex model</h3>
            <p className="mt-0.5 truncate text-xs text-stone-500">{chatName}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {availableModels.map((option) => {
            const selected = option.value === pickedModel
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setPickedModel(option.value)
                  if (!option.efforts.includes(picked)) setPicked('high')
                }}
                className={clsx(
                  'h-10 rounded-lg border px-3 text-sm font-medium transition-colors',
                  selected
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200'
                    : 'border-stone-200 text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-200 dark:hover:bg-stone-700',
                )}
                aria-pressed={selected}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {CODEX_EFFORT_OPTIONS.map((option) => {
            if (!availableEfforts.includes(option.value)) return null
            const selected = option.value === picked
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setPicked(option.value)}
                className={clsx(
                  'h-10 rounded-lg border px-3 text-sm font-medium transition-colors',
                  selected
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200'
                    : 'border-stone-200 text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-200 dark:hover:bg-stone-700',
                )}
                aria-pressed={selected}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="h-9 rounded-lg px-3 text-sm font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50 dark:text-stone-300 dark:hover:bg-stone-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(pickedModel, picked)}
            disabled={isPending}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-wait disabled:bg-emerald-300"
          >
            {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            Use {pickedModelLabel} · {pickedLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function AskAnotherModal({
  onCancel, onSubmit, text, setText, isPending, handoff, allowPro,
}: {
  onCancel: () => void
  onSubmit: (agent: AskAnotherAgent) => void
  text: string
  setText: (s: string) => void
  isPending: boolean
  handoff: ChatGptProHandoff | null
  allowPro: boolean
}) {
  const [picked, setPicked] = useState<AskAnotherAgent>('claude')
  const [copyStatus, setCopyStatus] = useState('')
  const executableOptions = [
    ...WRITABLE_MODEL_OPTIONS,
    { value: 'gemini', label: 'Gemini', detail: 'Antigravity review-only' },
  ] as const
  const askAnotherOptions = allowPro
    ? [
        ...executableOptions,
        { value: 'pro', label: 'Pro', detail: 'ChatGPT web · separate Chat allowance' } as const,
      ]
    : executableOptions
  const pickedDetail = askAnotherOptions.find((option) => option.value === picked)?.detail
  const pickedLabel = askAnotherOptions.find((option) => option.value === picked)?.label || picked

  if (handoff) {
    return (
      <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={onCancel}>
        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-600 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold">GPT Pro handoff ready</h3>
              <p className="text-xs text-stone-500 mt-0.5">
                AgentChat prepared this conversation and your question. Copy it, open ChatGPT, select Pro, and paste.
              </p>
            </div>
            <button onClick={onCancel} className="ml-auto text-stone-400 hover:text-stone-600" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            {handoff.warning}
          </p>
          <textarea
            readOnly
            value={handoff.prompt}
            rows={8}
            aria-label="GPT Pro prompt"
            onFocus={(event) => event.currentTarget.select()}
            className="w-full resize-none rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700 outline-none focus:ring-2 focus:ring-emerald-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200"
          />
          {copyStatus && <p className="mt-2 text-xs text-stone-600 dark:text-stone-300">{copyStatus}</p>}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={async () => {
                const copied = await writeClipboardText(handoff.prompt)
                setCopyStatus(copied ? 'Copied. Open ChatGPT next.' : 'Copy failed. Long-press the prompt above.')
              }}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              1. Copy prompt
            </button>
            <button
              type="button"
              onClick={() => void openExternalBrowser(handoff.url)}
              className="rounded-lg bg-stone-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black dark:bg-stone-100 dark:text-stone-900"
            >
              2. Open ChatGPT
            </button>
          </div>
          <p className="mt-3 text-[11px] text-stone-500">
            Paste GPT Pro's answer back here if you want the current agent to apply or challenge it.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={onCancel}>
      <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold">Get a second opinion</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              Uses the last ~20 messages in this lane or topic plus your question.
              Agent replies appear here; Pro prepares a handoff that you choose
              whether to paste into ChatGPT web.
            </p>
          </div>
          <button onClick={onCancel} className="ml-auto text-stone-400 hover:text-stone-600" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-1">
          {askAnotherOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setPicked(option.value)}
              className={clsx(
                'text-sm px-2 py-2 rounded-lg transition-colors',
                picked === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-stone-100 dark:bg-stone-700 hover:bg-stone-200 dark:hover:bg-stone-600',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-stone-500 mb-3">
          {picked === 'pro' ? 'Preparing for ' : 'Asking via '}
          <span className="font-medium">
            {pickedDetail}
          </span>
          {picked === 'pro'
            ? ' — nothing is sent to OpenAI until you copy and paste it.'
            : ' — ephemeral subprocess, no shared state.'}
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          autoFocus
          placeholder={`What do you want ${pickedLabel} to weigh in on?`}
          className="w-full text-sm px-3 py-2 rounded-lg bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onCancel} className="text-sm px-3 py-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-700">
            Cancel
          </button>
          <button
            onClick={() => onSubmit(picked)}
            disabled={!text.trim() || isPending}
            className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-stone-300 dark:disabled:bg-stone-700"
          >
            {isPending ? 'Preparing…' : picked === 'pro' ? 'Prepare Pro prompt' : `Ask ${pickedLabel}`}
          </button>
        </div>
      </div>
    </div>
  )
}
